import { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, FileText, Users, Building2, Send, Check, X, Trash2, Plus, Edit3, Rss, RefreshCw, Image as ImageIcon, Save, Upload, AlertCircle, Play, Pause, Power, ArrowUp, ArrowDown, Eye, EyeOff, LayoutList, Tag, ExternalLink, Link as LinkIcon } from "lucide-react";
import MenuManager from "@/components/MenuManager";
import JobsAdminPanel from "@/components/JobsAdminPanel";
import ImageUpload, { GalleryUpload } from "@/components/ImageUpload";
import CategorySelect from "@/components/CategorySelect";
import RichTextEditorPro from "@/components/RichTextEditorPro";
import MagazineLayoutFields from "@/components/MagazineLayoutFields";
import ProfilePreviewOverlay from "@/components/ProfilePreviewOverlay";
import MediaLibrary from "@/components/MediaLibrary";

export default function AdminDashboard() {
  const user = getUser();
  const [tab, setTab] = useState("overview");

  if (!user || (user.role !== "admin" && user.role !== "editor")) return <Navigate to="/login" />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <div className="overline text-sky-700 mb-1">Newsroom Console</div>
          <h1 className="headline text-3xl md:text-4xl font-bold">Editorial Dashboard</h1>
        </div>
        <div className="text-sm text-slate-500">Signed in as <span className="font-semibold text-slate-800">{user.name}</span> · {user.role}</div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
        {[
          { k: "overview", l: "Overview" },
          { k: "articles", l: "Articles" },
          { k: "create", l: "New Article" },
          { k: "jobs", l: "Jobs" },
          { k: "ai", l: "AI Queue" },
          { k: "airewrite", l: "AI Rewrite" },
          { k: "bulk", l: "Bulk Import" },
          { k: "feeds", l: "Auto-Crawler" },
          { k: "menu", l: "Menu" },
          { k: "categories", l: "Categories" },
          { k: "events", l: "Events" },
          { k: "directory", l: "Directory" },
          { k: "suppliers", l: "Suppliers" },
          { k: "edtech", l: "EdTech Companies" },
          { k: "pages", l: "Pages" },
          { k: "ads", l: "Ads" },
          { k: "media", l: "Media Library" },
          { k: "rankings", l: "Rankings" },
          { k: "claims", l: "Claims" },
          { k: "submissions", l: "Submissions & Press" },
          { k: "profile-submissions", l: "Profile Submissions" },
          { k: "ai-advisor", l: "AI Advisor" },
          { k: "leads", l: "Leads" },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.k ? "bg-sky-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-sky-100"}`} data-testid={`admin-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "articles" && <ArticlesList />}
      {tab === "create" && <CreateArticle />}
      {tab === "jobs" && <JobsAdminPanel />}
      {tab === "ai" && <AIQueue />}
      {tab === "airewrite" && <AIRewrite />}
      {tab === "bulk" && <BulkImport />}
      {tab === "feeds" && <FeedsManager />}
      {tab === "menu" && <MenuManager />}
      {tab === "categories" && <CategoriesManager />}
      {tab === "events" && <EventsManager />}
      {tab === "directory" && <DirectoryManager />}
      {tab === "suppliers" && <SuppliersManager />}
      {tab === "edtech" && <EdtechManager />}
      {tab === "pages" && <PagesManager />}
      {tab === "ads" && <AdsManager />}
      {tab === "media" && <MediaLibrary />}
      {tab === "rankings" && <RankingsManager />}
      {tab === "claims" && <ClaimsManager />}
      {tab === "submissions" && <SubmissionsQueue />}
      {tab === "profile-submissions" && <ProfileSubmissionsManager />}
      {tab === "ai-advisor" && <AiAdvisorAdmin />}
      {tab === "leads" && <AdminLeads />}
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-500">Loading…</div>;
  const stats = [
    { k: "Published", v: s.articles, icon: FileText, tone: "sky" },
    { k: "Drafts", v: s.drafts, icon: Edit3, tone: "amber" },
    { k: "Institutions", v: s.institutions, icon: Building2, tone: "emerald" },
    { k: "Suppliers", v: s.suppliers, icon: Building2, tone: "emerald" },
    { k: "Subscribers", v: s.subscribers, icon: Users, tone: "sky" },
    { k: "AI Pending", v: s.ai_pending, icon: Sparkles, tone: "amber" },
  ];
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="admin-stats">
        {stats.map(st => (
          <div key={st.k} className="card-soft p-5" data-testid={`stat-${st.k.toLowerCase()}`}>
            <div className={`w-9 h-9 rounded-xl grid place-items-center mb-3 ${st.tone === "sky" ? "bg-sky-100 text-sky-700" : st.tone === "amber" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}><st.icon className="w-4 h-4"/></div>
            <div className="font-editorial text-3xl font-bold text-slate-900">{st.v}</div>
            <div className="text-xs text-slate-500 mt-1">{st.k}</div>
          </div>
        ))}
      </div>
      <div className="card-soft p-6">
        <div className="overline text-sky-700 mb-3">Top Read Articles</div>
        <ul className="space-y-2">
          {s.top_articles.map(a => (
            <li key={a.slug} className="flex justify-between items-center text-sm py-2 border-b border-slate-50">
              <Link to={`/article/${a.slug}`} className="font-editorial font-semibold text-slate-800 title-link">{a.title}</Link>
              <span className="text-slate-500">{a.views} views</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ArticlesList() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // full article object being edited
  const load = () => api.get("/articles", { params: { limit: 50, status: "all" } }).then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!confirm("Delete this article?")) return;
    await api.delete(`/articles/${id}`);
    toast.success("Deleted"); load();
  };
  const openEdit = (a) => {
    // article row from list already has full fields
    setEditing(a);
  };
  return (
    <div className="space-y-4">
      <div className="card-soft overflow-hidden" data-testid="admin-articles-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr><th className="px-5 py-3">Title</th><th className="px-5 py-3">Categories</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Views</th><th className="px-5 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map(a => (
              <tr key={a.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`article-row-${a.id}`}>
                <td className="px-5 py-3 font-editorial font-semibold text-slate-900"><Link to={`/article/${a.slug}`} className="title-link">{a.title}</Link></td>
                <td className="px-5 py-3 text-xs text-slate-500">{(a.categories||[]).join(", ")}</td>
                <td className="px-5 py-3"><span className={`text-[10px] overline px-2 py-1 rounded-full ${a.status==="published"?"tag-green":"bg-slate-100 text-slate-600"}`}>{a.status || "published"}</span></td>
                <td className="px-5 py-3 text-slate-700">{a.views ?? 0}</td>
                <td className="px-5 py-3 text-right flex justify-end gap-1">
                  <button onClick={() => openEdit(a)} className="p-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700" data-testid={`edit-article-${a.id}`} title="Edit article"><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(a.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" data-testid={`delete-article-${a.id}`} title="Delete"><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No articles yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <EditArticleModal
          article={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditArticleModal({ article, onClose, onSaved }) {
  const [f, setF] = useState({
    title: article.title || "",
    subtitle: article.subtitle || "",
    content: article.content || "",
    excerpt: article.excerpt || "",
    featured_image: article.featured_image || "",
    author_name: article.author_name || "EduTime Desk",
    categories: article.categories || ["latest"],
    tags: article.tags || [],
    state: article.state || "",
    city: article.city || "",
    institution: article.institution || "",
    source_url: article.source_url || "",
    source_name: article.source_name || "",
    seo_title: article.seo_title || "",
    seo_description: article.seo_description || "",
    is_breaking: !!article.is_breaking,
    is_trending: !!article.is_trending,
    is_featured: !!article.is_featured,
    is_sponsored: !!article.is_sponsored,
    is_editors_pick: !!article.is_editors_pick,
    status: article.status || "published",
  });
  const [busy, setBusy] = useState(false);

  const save = async (publishNow = false) => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        tags: typeof f.tags === "string" ? f.tags.split(",").map(t => t.trim()).filter(Boolean) : f.tags,
        categories: typeof f.categories === "string" ? f.categories.split(",").map(t => t.trim()).filter(Boolean) : f.categories,
        status: publishNow ? "published" : f.status,
      };
      await api.put(`/articles/${article.id}`, payload);
      toast.success(publishNow ? "Article re-published" : "Article updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="edit-article-modal">
      <div className="bg-white rounded-3xl max-w-3xl w-full p-8 sm:p-10 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="edit-article-close"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">Edit article</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-6 truncate pr-10" title={article.title}>{article.title}</h2>
        <div className="space-y-4">
          <input required placeholder="Headline" value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-xl" data-testid="edit-article-title"/>
          <input placeholder="Subtitle" value={f.subtitle} onChange={e => setF({...f, subtitle: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <ImageUpload label="Featured image" value={f.featured_image} onChange={(v) => setF({...f, featured_image: v})} testId="edit-article-hero" height="h-48"/>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Content</label>
            <RichTextEditorPro value={f.content} onChange={(v) => setF({...f, content: v})} testId="edit-article-content" minHeight="320px"/>
          </div>
          <textarea rows={2} placeholder="Excerpt (short summary)" value={f.excerpt} onChange={e => setF({...f, excerpt: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          <CategorySelect value={f.categories} onChange={(v) => setF({...f, categories: v})} testId="edit-article-categories"/>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input placeholder="Tags (comma sep)" value={Array.isArray(f.tags) ? f.tags.join(",") : f.tags} onChange={e => setF({...f, tags: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="edit-article-tags"/>
            <input placeholder="Institution" value={f.institution} onChange={e => setF({...f, institution: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input placeholder="State" value={f.state} onChange={e => setF({...f, state: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Source name (e.g., IIT Bombay Press)" value={f.source_name} onChange={e => setF({...f, source_name: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input placeholder="Source URL" value={f.source_url} onChange={e => setF({...f, source_url: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="SEO title" value={f.seo_title} onChange={e => setF({...f, seo_title: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
            <input placeholder="SEO description" value={f.seo_description} onChange={e => setF({...f, seo_description: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            {["is_breaking","is_trending","is_featured","is_editors_pick","is_sponsored"].map(k => (
              <label key={k} className="flex items-center gap-2 text-slate-700"><input type="checkbox" checked={f[k]} onChange={e => setF({...f, [k]: e.target.checked})}/>{k.replace("is_","").replace(/_/g," ")}</label>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm text-slate-600">Status:</label>
            <select value={f.status} onChange={e => setF({...f, status: e.target.value})} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm" data-testid="edit-article-status">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={() => save(false)} disabled={busy} className="px-5 py-2.5 rounded-full bg-slate-100 hover:bg-sky-50 text-slate-800 text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid="edit-article-save"><Save className="w-4 h-4"/>{busy ? "Saving…" : "Save changes"}</button>
            <button onClick={() => save(true)} disabled={busy} className="px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid="edit-article-publish"><Send className="w-4 h-4"/>{busy ? "Publishing…" : "Save & Publish"}</button>
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateArticle() {
  const empty = {
    title: "", subtitle: "", content: "", excerpt: "", featured_image: "",
    author_name: "EduTime Desk", categories: ["latest"], tags: [], state: "", city: "", institution: "",
    seo_title: "", seo_description: "",
    is_breaking: false, is_trending: false, is_featured: false, is_sponsored: false, is_editors_pick: false,
    status: "published",
  };
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...f, tags: typeof f.tags === "string" ? f.tags.split(",").map(t => t.trim()).filter(Boolean) : f.tags,
                                categories: typeof f.categories === "string" ? f.categories.split(",").map(t => t.trim()).filter(Boolean) : f.categories };
      const r = await api.post("/articles", payload);
      toast.success("Article published");
      setF(empty);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="card-soft p-6 space-y-4" data-testid="create-article-form">
      <input required placeholder="Headline" value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-xl" data-testid="new-article-title"/>
      <input placeholder="Subtitle" value={f.subtitle} onChange={e => setF({...f, subtitle: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      <ImageUpload label="Featured image" value={f.featured_image} onChange={(v) => setF({...f, featured_image: v})} testId="new-article-hero" height="h-48"/>
      <div>
        <label className="text-xs text-slate-600 mb-1 block">Content</label>
        <RichTextEditorPro value={f.content} onChange={(v) => setF({...f, content: v})} testId="new-article-content" minHeight="360px"/>
      </div>
      <CategorySelect value={f.categories} onChange={(v) => setF({...f, categories: v})} testId="new-article-categories"/>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input placeholder="Tags (comma sep)" value={Array.isArray(f.tags) ? f.tags.join(",") : f.tags} onChange={e => setF({...f, tags: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Institution" value={f.institution} onChange={e => setF({...f, institution: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="State" value={f.state} onChange={e => setF({...f, state: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        {["is_breaking","is_trending","is_featured","is_editors_pick","is_sponsored"].map(k => (
          <label key={k} className="flex items-center gap-2 text-slate-700"><input type="checkbox" checked={f[k]} onChange={e => setF({...f, [k]: e.target.checked})}/>{k.replace("is_","").replace(/_/g," ")}</label>
        ))}
      </div>
      <button disabled={loading} className="px-6 py-3 rounded-full bg-sky-700 text-white font-semibold flex items-center gap-2" data-testid="publish-article-btn">
        <Plus className="w-4 h-4"/>{loading ? "Publishing…" : "Publish"}
      </button>
    </form>
  );
}

function AIQueue() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get("/ai/queue", { params: { status: filter } }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [filter]);
  return (
    <div className="space-y-5" data-testid="ai-queue-list">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs text-slate-500 mr-2">Filter:</span>
        {["pending","duplicate","approved","rejected","all"].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1 rounded-full text-xs font-semibold ${filter===s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`ai-filter-${s}`}>{s}</button>
        ))}
      </div>
      {items.length === 0 && <div className="card-soft p-8 text-center text-slate-500" data-testid="ai-queue-empty">No drafts here.</div>}
      {items.map(q => <QueueCard key={q.id} q={q} onChange={load} />)}
    </div>
  );
}

function QueueCard({ q, onChange }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({
    title: q.ai?.title || "",
    subtitle: q.ai?.subtitle || "",
    content: q.ai?.content || "",
    tags: (q.ai?.tags || []).join(", "),
  });
  const [busy, setBusy] = useState(null);
  const [tone, setTone] = useState("");

  const save = async () => {
    setBusy("save");
    try {
      const payload = { ...f, tags: f.tags.split(",").map(t => t.trim()).filter(Boolean) };
      await api.put(`/ai/queue/${q.id}`, payload);
      toast.success("Saved");
      setEditing(false); onChange();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(null); }
  };

  const regenerate = async () => {
    setBusy("regen");
    try { await api.post(`/ai/queue/${q.id}/regenerate`, { tone }); toast.success("Regenerated"); setEditing(false); onChange(); }
    catch (e) { toast.error("Regenerate failed"); }
    finally { setBusy(null); }
  };

  const genImage = async () => {
    setBusy("image");
    try { await api.post(`/ai/queue/${q.id}/generate-image`); toast.success("Hero image generated"); onChange(); }
    catch (e) { toast.error("Image generation failed"); }
    finally { setBusy(null); }
  };

  const approve = async () => {
    setBusy("approve");
    try { await api.post(`/ai/queue/${q.id}/approve`); toast.success("Published"); onChange(); }
    catch (e) { toast.error("Approve failed"); }
    finally { setBusy(null); }
  };

  const reject = async () => {
    setBusy("reject");
    try { await api.post(`/ai/queue/${q.id}/reject`); toast.success("Rejected"); onChange(); }
    catch (e) { toast.error("Reject failed"); }
    finally { setBusy(null); }
  };

  const statusBadge = {
    pending: "bg-amber-100 text-amber-800",
    duplicate: "bg-red-100 text-red-700",
    approved: "tag-green",
    rejected: "bg-slate-200 text-slate-700",
  };

  return (
    <div className="card-soft p-6" data-testid={`queue-card-${q.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className={`overline text-[10px] px-2.5 py-1 rounded-full ${statusBadge[q.status] || "bg-slate-100"}`}>{q.status}</span>
        {q.ai_failed && <span className="text-[10px] overline px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 flex items-center gap-1" title="AI rewrite failed on ingest — regenerate before publishing"><AlertCircle className="w-3 h-3"/>AI failed · needs regenerate</span>}
        {q.duplicate && <span className="text-[10px] overline px-2.5 py-1 rounded-full bg-red-100 text-red-700 flex items-center gap-1"><AlertCircle className="w-3 h-3"/>possible duplicate</span>}
        {q.institution && <span className="overline text-sky-700 text-[10px]">{q.institution}</span>}
        {(q.source_name || q.source_url) && (
          <span className="inline-flex items-center gap-1 text-[10px] overline px-2.5 py-1 rounded-full bg-sky-50 text-sky-800 border border-sky-100" data-testid={`queue-source-${q.id}`}>
            <LinkIcon className="w-3 h-3"/>
            <span className="font-semibold">Source:</span>
            {q.source_url ? (
              <a href={q.source_url} target="_blank" rel="noreferrer" className="underline flex items-center gap-0.5">
                {q.source_name || (() => { try { return new URL(q.source_url).hostname.replace(/^www\./, ""); } catch { return q.source_url; } })()}
                <ExternalLink className="w-3 h-3"/>
              </a>
            ) : <span>{q.source_name}</span>}
          </span>
        )}
        <span className="ml-auto text-[10px] text-slate-400">{new Date(q.created_at).toLocaleString()}</span>
      </div>

      {q.ai?.featured_image && (
        <div className="rounded-2xl overflow-hidden mb-4 bg-slate-100"><img src={q.ai.featured_image} alt="" className="w-full h-48 object-cover"/></div>
      )}

      {editing ? (
        <div className="space-y-3" data-testid={`edit-form-${q.id}`}>
          <input value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg font-bold"/>
          <input value={f.subtitle} onChange={e => setF({...f, subtitle: e.target.value})} placeholder="Subtitle" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <textarea rows={10} value={f.content} onChange={e => setF({...f, content: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-mono text-sm"/>
          <input value={f.tags} onChange={e => setF({...f, tags: e.target.value})} placeholder="tags, comma separated" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="px-4 py-2 rounded-full bg-sky-700 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid={`save-${q.id}`}><Save className="w-4 h-4"/>{busy==="save"?"Saving…":"Save"}</button>
            <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <h3 className="font-editorial text-xl font-bold text-slate-900">{q.ai?.title}</h3>
          {q.ai?.subtitle && <p className="text-slate-600 text-sm mt-2">{q.ai.subtitle}</p>}
          <div className="prose-editorial mt-4 text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: q.ai?.content || "" }}/>
          <div className="mt-4 flex flex-wrap gap-2 text-[11px]">{(q.ai?.tags || []).map(t => <span key={t} className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">#{t}</span>)}</div>
        </>
      )}

      {q.status === "pending" && !editing && (
        <div className="mt-5 space-y-3">
          <div className="flex flex-wrap gap-2">
            <input value={tone} onChange={e => setTone(e.target.value)} placeholder="Tone hint (optional, e.g., 'punchier')" className="flex-1 min-w-[220px] px-3 py-2 rounded-full bg-slate-50 border border-slate-200 outline-none focus:border-sky-400 text-xs" data-testid={`tone-${q.id}`}/>
            <button onClick={regenerate} disabled={busy} className="px-4 py-2 rounded-full bg-slate-100 hover:bg-sky-50 text-slate-800 text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid={`regenerate-${q.id}`}><RefreshCw className={`w-4 h-4 ${busy==="regen"?"animate-spin":""}`}/>{busy==="regen"?"Regenerating…":"Regenerate"}</button>
            <button onClick={() => setEditing(true)} className="px-4 py-2 rounded-full bg-slate-100 hover:bg-sky-50 text-slate-800 text-sm font-semibold flex items-center gap-1" data-testid={`edit-${q.id}`}><Edit3 className="w-4 h-4"/>Edit inline</button>
            <button onClick={genImage} disabled={busy} className="px-4 py-2 rounded-full bg-slate-100 hover:bg-emerald-50 text-slate-800 text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid={`gen-image-${q.id}`}><ImageIcon className={`w-4 h-4 ${busy==="image"?"animate-pulse":""}`}/>{busy==="image"?"Generating…":q.ai?.featured_image?"Regen Image":"Gen Hero Image"}</button>
          </div>
          <div className="flex gap-2">
            <button onClick={approve} disabled={busy} className="px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid={`approve-${q.id}`}><Check className="w-4 h-4"/>Approve & Publish</button>
            <button onClick={reject} disabled={busy} className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold flex items-center gap-1" data-testid={`reject-${q.id}`}><X className="w-4 h-4"/>Reject</button>
          </div>
        </div>
      )}
      {q.status === "duplicate" && (
        <div className="mt-4 flex gap-2 items-center">
          <button onClick={approve} disabled={busy} className="px-4 py-2 rounded-full bg-amber-600 text-white text-sm font-semibold flex items-center gap-1" data-testid={`dup-approve-${q.id}`}>Publish anyway</button>
          <button onClick={reject} disabled={busy} className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold" data-testid={`dup-reject-${q.id}`}>Discard</button>
        </div>
      )}
    </div>
  );
}

function AIRewrite() {
  const [form, setForm] = useState({ source_text: "", institution: "", source_url: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/ai/rewrite", form);
      toast.success("AI draft queued — review under 'AI Queue'");
      setForm({ source_text: "", institution: "", source_url: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="card-soft p-6 space-y-4" data-testid="ai-rewrite-form">
      <div className="flex items-center gap-2 mb-2 text-sky-700"><Sparkles className="w-4 h-4"/><span className="overline">GPT-5.2 · Auto Newsroom</span></div>
      <p className="text-sm text-slate-600">Paste source press release / news content. AI will rewrite in journalistic style, generate SEO metadata, and queue for editor approval.</p>
      <input placeholder="Institution name" value={form.institution} onChange={e => setForm({...form, institution: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="ai-rewrite-institution"/>
      <input placeholder="Source URL (optional)" value={form.source_url} onChange={e => setForm({...form, source_url: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      <textarea required rows={10} placeholder="Paste source press release or article content..." value={form.source_text} onChange={e => setForm({...form, source_text: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="ai-rewrite-source"/>
      <button disabled={loading} className="px-6 py-3 rounded-full bg-sky-700 text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="ai-rewrite-submit">
        <Send className="w-4 h-4"/>{loading ? "Generating (may take 20s)…" : "Generate Draft"}
      </button>
    </form>
  );
}

function SubmissionsQueue() {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/admin/submitted-news", { params: { status: "pending" } }).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => { await api.post(`/admin/submitted-news/${id}/approve`); toast.success("Approved & published to Articles"); load(); };
  const reject = async (id) => { await api.post(`/admin/submitted-news/${id}/reject`); toast.success("Rejected"); load(); };
  const onSaved = () => { setEditingId(null); load(); };
  if (!items.length) return <div className="card-soft p-8 text-center text-slate-500" data-testid="submissions-empty">No pending submissions. Public press releases and institution submissions land here for approval.</div>;
  return (
    <div className="space-y-4" data-testid="submissions-list">
      {items.map(s => (
        editingId === s.id
          ? <SubmissionEditor key={s.id} submission={s} onCancel={() => setEditingId(null)} onSaved={onSaved}/>
          : <SubmissionCard key={s.id} s={s} onApprove={approve} onReject={reject} onEdit={() => setEditingId(s.id)}/>
      ))}
    </div>
  );
}

function SubmissionCard({ s, onApprove, onReject, onEdit }) {
  const isPR = s.kind === "press_release";
  const imgSrc = s.featured_image ? (s.featured_image.startsWith("http") ? s.featured_image : `${process.env.REACT_APP_BACKEND_URL}${s.featured_image}`) : "";
  return (
    <div className="card-soft p-6" data-testid={`submission-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className={`overline text-[10px] px-2 py-1 rounded-full ${isPR ? "bg-emerald-50 text-emerald-800" : "bg-sky-50 text-sky-800"}`}>{isPR ? "Public press release" : "Institution submission"}</span>
        <span className="overline text-slate-500 text-[10px]">{s.institution_name || s.company_name} · submitted by {s.submitted_by || s.contact_name}</span>
        <span className="ml-auto text-[10px] text-slate-400">{s.created_at ? new Date(s.created_at).toLocaleString() : ""}</span>
      </div>
      <h3 className="font-editorial text-xl font-bold text-slate-900">{s.title}</h3>
      {s.subtitle && <p className="text-slate-600 text-sm mt-1">{s.subtitle}</p>}
      {(s.categories || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {(s.categories || []).map(c => <span key={c} className="text-[10px] overline px-2 py-1 rounded-full bg-sky-50 text-sky-700">{c}</span>)}
        </div>
      )}
      {imgSrc && (
        <div className="mt-3 flex items-center gap-3">
          <img src={imgSrc} alt="" className="w-40 h-28 object-cover rounded-xl border border-slate-100"/>
          <a href={imgSrc} download target="_blank" rel="noreferrer" className="text-xs text-sky-700 underline hover:text-sky-800" data-testid={`sub-image-download-${s.id}`}>Download image ↓</a>
        </div>
      )}
      <div className="prose-editorial mt-4 text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: (s.content || "").replace(/\n/g, "<br/>") }}/>
      {isPR && (
        <div className="mt-4 p-3 rounded-xl bg-sky-50/60 border border-sky-100 text-xs text-slate-700 grid md:grid-cols-2 gap-y-1 gap-x-4">
          <div><span className="font-semibold text-sky-800">Contact:</span> {s.contact_name}</div>
          <div><span className="font-semibold text-sky-800">Company:</span> {s.company_name}</div>
          <div><span className="font-semibold text-sky-800">Email:</span> <a className="underline" href={`mailto:${s.contact_email}`}>{s.contact_email}</a></div>
          {s.contact_phone && <div><span className="font-semibold text-sky-800">Phone:</span> <a className="underline" href={`tel:${s.contact_phone}`}>{s.contact_phone}</a></div>}
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={onEdit} className="px-4 py-2 rounded-full bg-sky-50 hover:bg-sky-100 text-sky-800 text-sm font-semibold flex items-center gap-1" data-testid={`sub-edit-${s.id}`}><Edit3 className="w-4 h-4"/>Edit</button>
        <button onClick={() => onApprove(s.id)} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold flex items-center gap-1" data-testid={`sub-approve-${s.id}`}><Check className="w-4 h-4"/>Approve & Publish</button>
        <button onClick={() => onReject(s.id)} className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold flex items-center gap-1" data-testid={`sub-reject-${s.id}`}><X className="w-4 h-4"/>Reject</button>
      </div>
    </div>
  );
}

function SubmissionEditor({ submission, onCancel, onSaved }) {
  const [f, setF] = useState({
    title: submission.title || "",
    subtitle: submission.subtitle || "",
    content: submission.content || "",
    featured_image: submission.featured_image || "",
    categories: submission.categories || ["press-releases"],
    tags: submission.tags || [],
  });
  const [busy, setBusy] = useState(false);

  const save = async (approveAfter = false) => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        tags: typeof f.tags === "string" ? f.tags.split(",").map(t => t.trim()).filter(Boolean) : f.tags,
      };
      await api.put(`/admin/submitted-news/${submission.id}`, payload);
      if (approveAfter) {
        await api.post(`/admin/submitted-news/${submission.id}/approve`);
        toast.success("Saved & published");
      } else {
        toast.success("Submission updated");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const imgSrc = f.featured_image ? (f.featured_image.startsWith("http") ? f.featured_image : `${process.env.REACT_APP_BACKEND_URL}${f.featured_image}`) : "";

  return (
    <div className="card-soft p-6 border-2 border-sky-200" data-testid={`submission-editor-${submission.id}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="overline text-sky-700">Editing submission</div>
        <button onClick={onCancel} className="ml-auto p-2 rounded-full hover:bg-slate-100" data-testid={`sub-editor-close-${submission.id}`}><X className="w-4 h-4"/></button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Headline</label>
          <input value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid={`sub-edit-title-${submission.id}`}/>
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Subtitle</label>
          <input value={f.subtitle} onChange={e => setF({...f, subtitle: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid={`sub-edit-subtitle-${submission.id}`}/>
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Content (HTML allowed)</label>
          <RichTextEditorPro value={f.content} onChange={(v) => setF({...f, content: v})} testId={`sub-edit-content-${submission.id}`} minHeight="240px"/>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-slate-600 block">Featured image</label>
            {imgSrc && <a href={imgSrc} download target="_blank" rel="noreferrer" className="text-xs text-sky-700 underline">Download current ↓</a>}
          </div>
          <ImageUpload value={f.featured_image} onChange={(v) => setF({...f, featured_image: v})} testId={`sub-edit-image-${submission.id}`} height="h-48"/>
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Categories</label>
          <CategorySelect value={f.categories} onChange={(v) => setF({...f, categories: v})} testId={`sub-edit-categories-${submission.id}`}/>
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Tags (comma separated)</label>
          <input value={Array.isArray(f.tags) ? f.tags.join(",") : f.tags} onChange={e => setF({...f, tags: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid={`sub-edit-tags-${submission.id}`}/>
        </div>
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          <button onClick={() => save(false)} disabled={busy} className="px-5 py-2.5 rounded-full bg-slate-100 hover:bg-sky-50 text-slate-800 text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid={`sub-edit-save-${submission.id}`}><Save className="w-4 h-4"/>{busy ? "Saving…" : "Save changes"}</button>
          <button onClick={() => save(true)} disabled={busy} className="px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid={`sub-edit-save-publish-${submission.id}`}><Check className="w-4 h-4"/>{busy ? "Publishing…" : "Save & Publish"}</button>
          <button onClick={onCancel} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AiAdvisorAdmin() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/advisor/settings").then(r => setSettings(r.data));
    api.get("/admin/advisor/stats").then(r => setStats(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      await api.put("/admin/advisor/settings", {
        enabled: settings.enabled,
        intro_text: settings.intro_text,
        system_prompt: settings.system_prompt,
      });
      toast.success("Advisor settings saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="ai-advisor-admin">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          ["Sessions started", stats?.sessions_started ?? "—"],
          ["Reports completed", stats?.sessions_completed ?? "—"],
          ["Completion rate", stats?.sessions_started ? `${Math.round(100 * (stats.sessions_completed || 0) / stats.sessions_started)}%` : "—"],
        ].map(([k, v]) => (
          <div key={k} className="card-soft p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{k}</div>
            <div className="text-2xl font-bold text-slate-900">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-soft p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-bold">Top interests</div>
          {stats?.top_interests?.length ? (
            <ul className="space-y-1 text-sm">
              {stats.top_interests.map(i => (
                <li key={i.label} className="flex justify-between"><span className="text-slate-700">{i.label}</span><span className="text-slate-500">{i.count}</span></li>
              ))}
            </ul>
          ) : <div className="text-sm text-slate-500">No data yet.</div>}
        </div>
        <div className="card-soft p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-bold">Top career goals</div>
          {stats?.top_careers?.length ? (
            <ul className="space-y-1 text-sm">
              {stats.top_careers.map(i => (
                <li key={i.label} className="flex justify-between"><span className="text-slate-700">{i.label}</span><span className="text-slate-500">{i.count}</span></li>
              ))}
            </ul>
          ) : <div className="text-sm text-slate-500">No data yet.</div>}
        </div>
      </div>

      {/* Settings */}
      <div className="card-soft p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">AI Advisor status</div>
            <div className="text-xs text-slate-500">Turn the advisor on/off site-wide.</div>
          </div>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={!!settings.enabled} onChange={e => setSettings({...settings, enabled: e.target.checked})} data-testid="advisor-enable"/>
            <span className="text-sm font-semibold">{settings.enabled ? "Enabled" : "Disabled"}</span>
          </label>
        </div>

        <div>
          <label className="text-xs font-bold text-slate-700 mb-1 block">Introductory text (shown on landing page)</label>
          <textarea rows={3} value={settings.intro_text || ""} onChange={e => setSettings({...settings, intro_text: e.target.value})}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm bg-white" data-testid="advisor-intro"/>
        </div>

        <div>
          <label className="text-xs font-bold text-slate-700 mb-1 block">AI System Prompt <span className="text-slate-500 font-normal">(advanced)</span></label>
          <textarea rows={12} value={settings.system_prompt || ""} onChange={e => setSettings({...settings, system_prompt: e.target.value})}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-xs font-mono bg-slate-50" data-testid="advisor-prompt"/>
        </div>

        <button onClick={save} disabled={busy}
          className="px-6 py-2.5 rounded-full bg-slate-900 text-yellow-300 text-sm font-bold disabled:opacity-60"
          data-testid="advisor-save">
          {busy ? "Saving…" : "Save Advisor Settings"}
        </button>
      </div>
    </div>
  );
}


function ProfileSubmissionsManager() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState(null);   // draft copy of the selected row while editing
  const [saving, setSaving] = useState(false);

  const load = () => {
    const params = status ? { params: { status } } : {};
    api.get("/admin/profile-submissions", params).then(r => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const openRow = (row) => { setSelected(row); setEdit({ ...row }); };
  const closeRow = () => { setSelected(null); setEdit(null); };

  const setF = (k, v) => setEdit(p => ({ ...p, [k]: v }));

  const updateStatus = async (id, s) => {
    try {
      await api.put(`/admin/profile-submissions/${id}/status?status=${s}`);
      toast.success(`Marked ${s}`);
      closeRow();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const saveEdits = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      const payload = {
        profile_type: edit.profile_type, name: edit.name, address: edit.address, city: edit.city,
        state: edit.state, pin: edit.pin, phone: edit.phone, email: edit.email, website: edit.website,
        logo: edit.logo, images: edit.images || [], description: edit.description,
        courses_offered: edit.courses_offered, fees: edit.fees, head_of_institution: edit.head_of_institution,
        tariff_accepted: !!edit.tariff_accepted, payment_mode: edit.payment_mode || "",
        submitter_name: edit.submitter_name, submitter_email: edit.submitter_email, submitter_phone: edit.submitter_phone,
      };
      const { data } = await api.put(`/admin/profile-submissions/${edit.id}`, payload);
      toast.success("Submission updated");
      load();
      closeRow();
      // suppress unused-var warning
      void data;
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try { await api.delete(`/admin/profile-submissions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const statusPill = (s) => {
    const cls = s === "approved" ? "bg-emerald-100 text-emerald-700"
              : s === "rejected" ? "bg-rose-100 text-rose-700"
              : "bg-amber-100 text-amber-700";
    return <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${cls}`}>{s}</span>;
  };

  const INP = "w-full px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm bg-white";

  return (
    <div className="space-y-4" data-testid="profile-submissions-manager">
      <div className="card-soft p-6 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="overline text-sky-700 mb-1">Directory intake</div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">"Add My Profile" submissions</h3>
          <p className="text-sm text-slate-600 mt-1">Institutions, EdTech companies and suppliers who submitted their details for inclusion in the EduTime directory. Click a row to edit any field before approving.</p>
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm" data-testid="ps-filter">
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="card-soft overflow-hidden" data-testid="profile-submissions-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">City</th>
              <th className="px-5 py-3">Submitter</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50/40">
                <td className="px-5 py-3 font-semibold text-slate-900">{it.name}</td>
                <td className="px-5 py-3 capitalize text-slate-600">{it.profile_type}</td>
                <td className="px-5 py-3 text-slate-500">{it.city}{it.state ? `, ${it.state}` : ""}</td>
                <td className="px-5 py-3 text-slate-500">
                  <div>{it.submitter_name}</div>
                  <div className="text-xs">{it.submitter_email}</div>
                </td>
                <td className="px-5 py-3">{statusPill(it.status)}</td>
                <td className="px-5 py-3 flex gap-2">
                  <button onClick={() => openRow(it)} className="px-3 py-1 rounded-full bg-sky-50 hover:bg-sky-100 text-sky-800 text-xs font-semibold flex items-center gap-1" data-testid={`ps-edit-${it.id}`}><Edit3 className="w-3 h-3"/>Edit</button>
                  <button onClick={() => del(it.id)} className="px-2 py-1 rounded-full bg-rose-50 text-rose-700 text-xs" data-testid={`ps-del-${it.id}`}><Trash2 className="w-3 h-3"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No submissions.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && edit && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={closeRow}>
          <div className="bg-white rounded-3xl max-w-3xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="ps-edit-modal">
            <button onClick={closeRow} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4"/></button>
            <div className="overline text-sky-700 mb-1 capitalize">Edit {selected.profile_type} submission</div>
            <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-5">{selected.name}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Profile type</div>
                <select value={edit.profile_type || ""} onChange={e => setF("profile_type", e.target.value)} className={INP} data-testid="ps-edit-profile_type">
                  <option value="college">College</option>
                  <option value="school">School</option>
                  <option value="edtech">EdTech</option>
                  <option value="supplier">Supplier</option>
                </select>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Name</div>
                <input value={edit.name || ""} onChange={e => setF("name", e.target.value)} className={INP} data-testid="ps-edit-name"/>
              </label>
              <label className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Address</div>
                <input value={edit.address || ""} onChange={e => setF("address", e.target.value)} className={INP} data-testid="ps-edit-address"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">City</div>
                <input value={edit.city || ""} onChange={e => setF("city", e.target.value)} className={INP} data-testid="ps-edit-city"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">State</div>
                <input value={edit.state || ""} onChange={e => setF("state", e.target.value)} className={INP} data-testid="ps-edit-state"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">PIN</div>
                <input value={edit.pin || ""} onChange={e => setF("pin", e.target.value)} className={INP} data-testid="ps-edit-pin"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Phone</div>
                <input value={edit.phone || ""} onChange={e => setF("phone", e.target.value)} className={INP} data-testid="ps-edit-phone"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Email</div>
                <input value={edit.email || ""} onChange={e => setF("email", e.target.value)} className={INP} data-testid="ps-edit-email"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Website</div>
                <input value={edit.website || ""} onChange={e => setF("website", e.target.value)} className={INP} data-testid="ps-edit-website"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Head of Institution</div>
                <input value={edit.head_of_institution || ""} onChange={e => setF("head_of_institution", e.target.value)} className={INP} data-testid="ps-edit-head"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Fees</div>
                <input value={edit.fees || ""} onChange={e => setF("fees", e.target.value)} className={INP} data-testid="ps-edit-fees"/>
              </label>
              <label className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Courses Offered</div>
                <input value={edit.courses_offered || ""} onChange={e => setF("courses_offered", e.target.value)} className={INP} data-testid="ps-edit-courses"/>
              </label>
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Description</div>
              <textarea rows={4} value={edit.description || ""} onChange={e => setF("description", e.target.value)} className={`${INP} min-h-[100px]`} data-testid="ps-edit-description"/>
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Logo</div>
              <ImageUpload value={edit.logo || ""} onChange={(v) => setF("logo", v)} testId="ps-edit-logo" height="h-32"/>
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Gallery images</div>
              <GalleryUpload value={edit.images || []} onChange={(v) => setF("images", v)} testId="ps-edit-images"/>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5 text-sm">
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Tariff Accepted</div>
                <select value={edit.tariff_accepted ? "yes" : "no"} onChange={e => setF("tariff_accepted", e.target.value === "yes")} className={INP} data-testid="ps-edit-tariff">
                  <option value="no">No</option>
                  <option value="yes">Yes — ₹8,000/year</option>
                </select>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Payment Mode</div>
                <select value={edit.payment_mode || ""} onChange={e => setF("payment_mode", e.target.value)} className={INP} data-testid="ps-edit-payment">
                  <option value="">—</option>
                  <option value="pay_now">Pay now (Razorpay)</option>
                  <option value="invoice">Invoice requested</option>
                </select>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Submitter Name</div>
                <input value={edit.submitter_name || ""} onChange={e => setF("submitter_name", e.target.value)} className={INP} data-testid="ps-edit-sub-name"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Submitter Email</div>
                <input value={edit.submitter_email || ""} onChange={e => setF("submitter_email", e.target.value)} className={INP} data-testid="ps-edit-sub-email"/>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Submitter Phone</div>
                <input value={edit.submitter_phone || ""} onChange={e => setF("submitter_phone", e.target.value)} className={INP} data-testid="ps-edit-sub-phone"/>
              </label>
            </div>

            <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-slate-100">
              <button onClick={saveEdits} disabled={saving} className="px-5 py-2.5 rounded-full bg-slate-900 text-yellow-300 text-sm font-bold flex items-center gap-1 disabled:opacity-60" data-testid="ps-save"><Save className="w-4 h-4"/>{saving ? "Saving…" : "Save changes"}</button>
              <button onClick={() => updateStatus(selected.id, "approved")} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold flex items-center gap-1" data-testid="ps-approve"><Check className="w-4 h-4"/>Approve</button>
              <button onClick={() => updateStatus(selected.id, "rejected")} className="px-4 py-2 rounded-full bg-rose-600 text-white text-sm font-semibold flex items-center gap-1" data-testid="ps-reject"><X className="w-4 h-4"/>Reject</button>
              <button onClick={() => updateStatus(selected.id, "pending")} className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold">Reset to Pending</button>
              <button onClick={closeRow} className="ml-auto px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminLeads() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/leads").then(r => setItems(r.data)); }, []);
  if (!items.length) return <div className="card-soft p-8 text-center text-slate-500" data-testid="admin-leads-empty">No leads yet.</div>;
  return (
    <div className="card-soft overflow-hidden" data-testid="admin-leads-list">
      <table className="w-full text-sm">
        <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
          <tr><th className="px-5 py-3">Target</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Message</th><th className="px-5 py-3">When</th></tr>
        </thead>
        <tbody>
          {items.map(l => (
            <tr key={l.id} className="border-b border-slate-50">
              <td className="px-5 py-3 font-editorial font-semibold">{l.target_name}</td>
              <td className="px-5 py-3 text-xs"><span className={`overline px-2 py-1 rounded-full ${l.target_type === "institution" ? "tag-blue" : "tag-green"}`}>{l.target_type}</span></td>
              <td className="px-5 py-3">{l.name}</td>
              <td className="px-5 py-3"><a href={`mailto:${l.email}`} className="text-sky-700">{l.email}</a></td>
              <td className="px-5 py-3 text-slate-600 max-w-md">{l.message}</td>
              <td className="px-5 py-3 text-xs text-slate-500">{new Date(l.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function BulkImport() {
  const [mode, setMode] = useState("urls");  // urls, texts
  const [text, setText] = useState("");
  const [institution, setInstitution] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      let entries = [];
      if (mode === "urls") {
        entries = text.split(/\r?\n/).map(u => u.trim()).filter(Boolean).map(u => ({ url: u, institution }));
      } else {
        // separate by --- separator
        const chunks = text.split(/\n---+\n/).map(t => t.trim()).filter(Boolean);
        entries = chunks.map(t => ({ source_text: t, institution }));
      }
      if (!entries.length) { toast.error("Nothing to import"); setBusy(false); return; }
      const { data } = await api.post("/ai/bulk-import", { entries });
      setResult(data);
      toast.success(`Queued ${data.queued}, duplicates ${data.duplicates}, failed ${data.failed}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="bulk-import">
      <div className="card-soft p-6">
        <div className="flex items-center gap-2 mb-2 text-sky-700"><Upload className="w-4 h-4"/><span className="overline">Bulk import into AI Queue</span></div>
        <p className="text-sm text-slate-600 mb-4">Paste up to 25 press-release URLs (one per line) OR raw texts separated by a line containing only <code className="bg-slate-100 px-1 rounded">---</code>. GPT-5.2 will rewrite each, dedupe against existing stories, and queue them for editorial review.</p>
        <div className="flex gap-2 mb-3">
          <button onClick={() => setMode("urls")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${mode==="urls"?"bg-sky-700 text-white":"bg-slate-100 text-slate-700"}`} data-testid="bulk-mode-urls">URLs</button>
          <button onClick={() => setMode("texts")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${mode==="texts"?"bg-sky-700 text-white":"bg-slate-100 text-slate-700"}`} data-testid="bulk-mode-texts">Raw texts</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input placeholder="Default institution (optional)" value={institution} onChange={e => setInstitution(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <textarea required rows={10} value={text} onChange={e => setText(e.target.value)}
            placeholder={mode==="urls" ? "https://iitb.ac.in/press/2026-01-01\nhttps://cbse.gov.in/results-notice\n..." : "Full press release text one\n---\nFull press release text two"}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-mono text-sm" data-testid="bulk-text"/>
          <button disabled={busy} className="px-6 py-3 rounded-full bg-sky-700 text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="bulk-submit"><Upload className="w-4 h-4"/>{busy?"Importing… (~10-20s / item)":"Start Bulk Import"}</button>
        </form>
      </div>

      {result && (
        <div className="card-soft p-6" data-testid="bulk-result">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm">Queued <strong className="text-emerald-700">{result.queued}</strong></span>
            <span className="text-sm">Duplicates <strong className="text-amber-700">{result.duplicates}</strong></span>
            <span className="text-sm">Failed <strong className="text-red-700">{result.failed}</strong></span>
          </div>
          <ul className="text-sm divide-y divide-slate-100">
            {result.items.map((it, i) => (
              <li key={i} className="py-2 flex justify-between gap-3">
                <span className="text-slate-700 truncate">{it.title || it.input?.url || "—"}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${it.status==="pending"?"bg-emerald-100 text-emerald-800": it.status==="duplicate"?"bg-amber-100 text-amber-800":"bg-red-100 text-red-700"}`}>{it.status}{it.reason?`: ${it.reason}`:""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FeedsManager() {
  const [feeds, setFeeds] = useState([]);
  const [f, setF] = useState({ name: "", url: "", institution: "", enabled: true });
  const [busy, setBusy] = useState(null);
  const [sched, setSched] = useState(null);

  const load = () => api.get("/admin/feeds").then(r => setFeeds(r.data));
  const loadSched = () => api.get("/admin/scheduler").then(r => setSched(r.data)).catch(() => {});
  useEffect(() => { load(); loadSched(); }, []);

  const startSched = async () => {
    setBusy("sched");
    try { await api.post("/admin/scheduler/start"); toast.success("Auto-crawler started"); await loadSched(); }
    catch (e) { toast.error(e.response?.data?.detail || "Start failed"); }
    finally { setBusy(null); }
  };
  const stopSched = async () => {
    setBusy("sched");
    try { await api.post("/admin/scheduler/stop"); toast.success("Auto-crawler stopped"); await loadSched(); }
    catch (e) { toast.error(e.response?.data?.detail || "Stop failed"); }
    finally { setBusy(null); }
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy("add");
    try { await api.post("/admin/feeds", f); toast.success("Feed added"); setF({ name: "", url: "", institution: "", enabled: true }); load(); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(null); }
  };

  const del = async (id) => {
    if (!confirm("Delete this feed?")) return;
    await api.delete(`/admin/feeds/${id}`); toast.success("Deleted"); load();
  };

  const crawl = async (id) => {
    setBusy("c-" + id);
    try {
      const { data } = await api.post(`/admin/feeds/${id}/crawl`);
      toast.success(`Queued ${data.queued}, dup ${data.duplicates}, failed ${data.failed}`);
      load();
    } catch (e) { toast.error("Crawl failed"); }
    finally { setBusy(null); }
  };

  const crawlAll = async () => {
    setBusy("all");
    try {
      const { data } = await api.post(`/admin/feeds/crawl-all`);
      const totals = data.results.reduce((a, r) => ({q: a.q+r.queued, d: a.d+r.duplicates, f: a.f+r.failed}), {q:0,d:0,f:0});
      toast.success(`All done — queued ${totals.q}, dup ${totals.d}, failed ${totals.f}`);
      load();
    } catch (e) { toast.error("Crawl all failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="feeds-manager">
      {/* Scheduler control */}
      <div className="card-soft p-6 flex flex-wrap items-center gap-4" data-testid="scheduler-panel">
        <div className={`w-10 h-10 rounded-2xl grid place-items-center ${sched?.running ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
          <Power className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-[10px] text-sky-700 mb-0.5">Auto-Crawler Schedule</div>
          <div className="font-editorial font-bold text-slate-900">
            {sched?.running ? "Running" : "Stopped"}
            <span className="ml-2 text-xs text-slate-500 font-normal">
              {sched?.cron || "daily at 06:00 UTC"}
              {sched?.next_run_time && ` · next: ${new Date(sched.next_run_time).toLocaleString()}`}
            </span>
          </div>
        </div>
        {sched?.running ? (
          <button onClick={stopSched} disabled={busy==="sched"} className="px-5 py-2.5 rounded-full bg-slate-100 hover:bg-red-50 text-slate-800 hover:text-red-700 font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="scheduler-stop-btn">
            <Pause className="w-4 h-4"/>Stop Auto-Crawler
          </button>
        ) : (
          <button onClick={startSched} disabled={busy==="sched"} className="px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="scheduler-start-btn">
            <Play className="w-4 h-4"/>Start Auto-Crawler
          </button>
        )}
      </div>

      <div className="card-soft p-6">
        <div className="flex items-center gap-2 mb-2 text-sky-700"><Rss className="w-4 h-4"/><span className="overline">Self-feeding newsroom</span></div>
        <p className="text-sm text-slate-600 mb-4">Add institution press-release URLs. When the auto-crawler is running, it visits each source daily (06:00 UTC), extracts content, runs GPT-5.2 rewrite, dedupes against existing stories, and drops drafts into the AI Queue. If AI rewrite fails, the raw extracted text still lands in the queue so nothing is lost. You can also crawl on demand.</p>
        <form onSubmit={add} className="grid md:grid-cols-4 gap-2">
          <input required placeholder="Feed name" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="feed-name-input"/>
          <input required placeholder="https://..." value={f.url} onChange={e => setF({...f, url: e.target.value})} className="md:col-span-2 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="feed-url-input"/>
          <input placeholder="Institution (optional)" value={f.institution} onChange={e => setF({...f, institution: e.target.value})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          <button disabled={busy==="add"} className="md:col-span-4 px-6 py-2.5 rounded-full bg-sky-700 text-white font-semibold text-sm w-fit flex items-center gap-1 disabled:opacity-60" data-testid="feed-add-btn"><Plus className="w-4 h-4"/>Add Source</button>
        </form>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-editorial text-xl font-bold">Sources ({feeds.length})</h3>
        {feeds.length > 0 && <button onClick={crawlAll} disabled={busy==="all"} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid="crawl-all-btn"><Play className={`w-4 h-4 ${busy==="all"?"animate-pulse":""}`}/>{busy==="all"?"Crawling all…":"Crawl All Now"}</button>}
      </div>

      <div className="card-soft overflow-hidden">
        {feeds.length === 0 ? <div className="p-8 text-center text-slate-500" data-testid="feeds-empty">No sources added yet.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
              <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">URL</th><th className="px-5 py-3">Last Crawl</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th></tr>
            </thead>
            <tbody>
              {feeds.map(fe => (
                <tr key={fe.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`feed-row-${fe.id}`}>
                  <td className="px-5 py-3 font-editorial font-semibold text-slate-900">{fe.name}</td>
                  <td className="px-5 py-3 text-xs text-slate-600 max-w-md truncate"><a href={fe.url} target="_blank" rel="noreferrer" className="text-sky-700">{fe.url}</a></td>
                  <td className="px-5 py-3 text-xs text-slate-500">{fe.last_crawled_at ? new Date(fe.last_crawled_at).toLocaleString() : "Never"}</td>
                  <td className="px-5 py-3"><span className={`text-[10px] overline px-2 py-1 rounded-full ${fe.last_status==="ok"?"tag-green": fe.last_status==="never"?"bg-slate-100 text-slate-500":"bg-red-100 text-red-700"}`}>{fe.last_status || "never"}</span></td>
                  <td className="px-5 py-3 text-right flex justify-end gap-1">
                    <button onClick={() => crawl(fe.id)} disabled={busy===`c-${fe.id}`} className="p-2 rounded-lg bg-sky-100 hover:bg-sky-200 text-sky-700 disabled:opacity-50" data-testid={`crawl-${fe.id}`}><Play className={`w-4 h-4 ${busy===`c-${fe.id}`?"animate-pulse":""}`}/></button>
                    <button onClick={() => del(fe.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" data-testid={`del-feed-${fe.id}`}><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function CategoriesManager() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ slug: "", name: "", order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/categories").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      if (editingId) {
        await api.put(`/admin/categories/${editingId}`, f);
        toast.success("Updated");
      } else {
        await api.post("/admin/categories", f);
        toast.success("Category added");
      }
      setF({ slug: "", name: "", order: 0 }); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const edit = (c) => { setEditingId(c.id); setF({ slug: c.slug, name: c.name, order: c.order || 0 }); };
  const cancel = () => { setEditingId(null); setF({ slug: "", name: "", order: 0 }); };
  const del = async (id) => {
    if (!confirm("Delete this category? Articles tagged with it will keep the slug.")) return;
    await api.delete(`/admin/categories/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-6" data-testid="categories-manager">
      <div className="card-soft p-6">
        <div className="flex items-center gap-2 mb-2 text-sky-700"><Tag className="w-4 h-4"/><span className="overline">{editingId ? "Edit category" : "Add category"}</span></div>
        <p className="text-sm text-slate-600 mb-4">Categories are used when creating articles and shown on <code className="bg-slate-100 px-1 rounded text-xs">/category/&lt;slug&gt;</code> pages. Slug should be lowercase-hyphen (e.g. <code className="bg-slate-100 px-1 rounded text-xs">study-abroad</code>).</p>
        <form onSubmit={submit} className="grid md:grid-cols-4 gap-2">
          <input required placeholder="Slug (e.g. cyber-security)" value={f.slug} onChange={e => setF({...f, slug: e.target.value.toLowerCase().replace(/\s+/g, "-")})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="cat-slug-input"/>
          <input required placeholder="Display name" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="md:col-span-2 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="cat-name-input"/>
          <input type="number" placeholder="Order" value={f.order} onChange={e => setF({...f, order: parseInt(e.target.value) || 0})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          <div className="md:col-span-4 flex gap-2">
            <button disabled={busy==="save"} className="px-5 py-2.5 rounded-full bg-sky-700 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="cat-save-btn"><Plus className="w-4 h-4"/>{editingId ? "Save changes" : "Add category"}</button>
            {editingId && <button type="button" onClick={cancel} className="px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>}
          </div>
        </form>
      </div>

      <div className="card-soft overflow-hidden" data-testid="cat-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr><th className="px-5 py-3 w-14">#</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">Slug</th><th className="px-5 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((c, idx) => (
              <tr key={c.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`cat-row-${c.slug}`}>
                <td className="px-5 py-3 text-slate-500">{idx + 1}</td>
                <td className="px-5 py-3 font-editorial font-semibold text-slate-900">{c.name}</td>
                <td className="px-5 py-3 text-xs text-slate-600"><Link to={`/category/${c.slug}`} target="_blank" className="text-sky-700">{c.slug}</Link></td>
                <td className="px-5 py-3 flex justify-end gap-1">
                  <button onClick={() => edit(c)} className="p-2 rounded-lg bg-slate-100 hover:bg-sky-100 text-slate-600" data-testid={`cat-edit-${c.slug}`}><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(c.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" data-testid={`cat-del-${c.slug}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500">No categories yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EventsManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("upcoming"); // upcoming | past | all
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/events").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const shown = items.filter(ev => {
    if (filter === "all") return true;
    const isPast = ev.date && ev.date < today;
    return filter === "past" ? isPast : !isPast;
  });

  const upcomingCount = items.filter(ev => !(ev.date && ev.date < today)).length;
  const pastCount = items.filter(ev => ev.date && ev.date < today).length;

  const del = async (id) => {
    if (!confirm("Delete this event?")) return;
    try { await api.delete(`/events/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="events-manager">
      <div className="card-soft p-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-sky-700 mb-1">Education Events</div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">Conferences, webinars, admission fairs, workshops</h3>
          <p className="text-sm text-slate-600 mt-1">Only <strong>upcoming</strong> events appear on the homepage and public /events page. Past events stay here for your records.</p>
        </div>
        <button onClick={() => setEditing("new")} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1" data-testid="event-add-btn"><Plus className="w-4 h-4"/>Add event</button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="event-filter-tabs">
        {[
          { k: "upcoming", l: `Upcoming (${upcomingCount})` },
          { k: "past", l: `Past (${pastCount})` },
          { k: "all", l: `All (${items.length})` },
        ].map(t => (
          <button key={t.k} onClick={() => setFilter(t.k)} className={`px-4 py-2 rounded-full text-xs font-semibold ${filter===t.k?"bg-sky-700 text-white":"bg-slate-100 text-slate-700 hover:bg-sky-50"}`} data-testid={`event-filter-${t.k}`}>{t.l}</button>
        ))}
      </div>

      <div className="card-soft overflow-hidden" data-testid="events-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr>
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3">Location</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(ev => {
              const isPast = ev.date && ev.date < today;
              return (
              <tr key={ev.id} className={`border-b border-slate-50 hover:bg-sky-50/30 ${isPast?"opacity-60":""}`} data-testid={`event-row-${ev.id}`}>
                <td className="px-5 py-3 font-editorial font-semibold text-slate-900 flex items-center gap-2">{ev.title}{isPast && <span className="text-[10px] overline px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">past</span>}</td>
                <td className="px-5 py-3 text-slate-700">{ev.date}</td>
                <td className="px-5 py-3 text-slate-600">{ev.location}</td>
                <td className="px-5 py-3"><span className="text-[10px] overline px-2 py-1 rounded-full bg-sky-50 text-sky-700">{ev.type || "event"}</span></td>
                <td className="px-5 py-3 text-right flex justify-end gap-1">
                  <button onClick={() => setEditing(ev)} className="p-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700" title="Edit" data-testid={`event-edit-${ev.id}`}><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(ev.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" title="Delete" data-testid={`event-delete-${ev.id}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            );})}
            {shown.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">{filter === "upcoming" ? "No upcoming events. Click \"Add event\" to create one." : filter === "past" ? "No past events." : "No events yet."}</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <EventEditor
          event={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EventEditor({ event, onClose, onSaved }) {
  const isNew = !event;
  const [f, setF] = useState({
    title: event?.title || "",
    description: event?.description || "",
    date: event?.date || "",
    location: event?.location || "",
    type: event?.type || "conference",
    image: event?.image || "",
    url: event?.url || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.title.trim() || !f.date.trim() || !f.location.trim() || !f.description.trim()) {
      toast.error("Title, description, date and location are required");
      return;
    }
    setBusy(true);
    try {
      if (isNew) await api.post("/events", f);
      else await api.put(`/events/${event.id}`, f);
      toast.success(isNew ? "Event created" : "Event updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="event-editor-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="event-close"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">{isNew ? "Add event" : "Edit event"}</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-6">{isNew ? "New event" : event.title}</h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Title</label>
            <input required autoFocus value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="event-title"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Description</label>
            <textarea required rows={4} value={f.description} onChange={e => setF({...f, description: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="event-description"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Date (YYYY-MM-DD)</label>
              <input required type="date" value={f.date} onChange={e => setF({...f, date: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="event-date"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Location</label>
              <input required value={f.location} onChange={e => setF({...f, location: e.target.value})} placeholder="e.g., Mumbai / Online" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="event-location"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Type</label>
              <select value={f.type} onChange={e => setF({...f, type: e.target.value})} className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm" data-testid="event-type">
                <option value="conference">Conference</option>
                <option value="webinar">Webinar</option>
                <option value="fair">Fair / Expo</option>
                <option value="workshop">Workshop</option>
                <option value="summit">Summit</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">External URL (optional)</label>
              <input value={f.url} onChange={e => setF({...f, url: e.target.value})} placeholder="https://..." className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="event-url"/>
            </div>
          </div>
          <ImageUpload label="Event image (optional)" value={f.image} onChange={(v) => setF({...f, image: v})} testId="event-image" height="h-40"/>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="event-save"><Save className="w-4 h-4"/>{busy ? "Saving…" : (isNew ? "Create event" : "Save changes")}</button>
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function DirectoryManager() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [editing, setEditing] = useState(null); // institution obj or "new"
  const load = () => api.get("/admin/institutions", { params: type !== "all" ? { type } : {} }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [type]);

  const del = async (id) => {
    if (!confirm("Delete this institution profile? This cannot be undone.")) return;
    try { await api.delete(`/institutions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="directory-manager">
      <div className="card-soft p-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-sky-700 mb-1">Directory</div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">Schools · Colleges · Universities</h3>
          <p className="text-sm text-slate-600 mt-1">Add institution profiles. They appear at <code className="bg-slate-100 px-1 rounded text-xs">/schools</code>, <code className="bg-slate-100 px-1 rounded text-xs">/colleges</code> and their public profile pages.</p>
        </div>
        <select value={type} onChange={e => setType(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm" data-testid="dir-filter-type">
          <option value="all">All types</option>
          <option value="school">Schools (K-12)</option>
          <option value="college">Colleges / Universities</option>
        </select>
        <button onClick={() => setEditing("new")} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1" data-testid="dir-add-btn"><Plus className="w-4 h-4"/>Add profile</button>
      </div>

      <div className="card-soft overflow-hidden" data-testid="dir-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Location</th>
              <th className="px-5 py-3">Board / Affiliation</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`dir-row-${it.id}`}>
                <td className="px-5 py-3 font-editorial font-semibold text-slate-900"><Link to={`/institution/${it.slug}`} className="title-link">{it.name}</Link></td>
                <td className="px-5 py-3"><span className={`text-[10px] overline px-2 py-1 rounded-full ${it.type === "college" ? "bg-emerald-50 text-emerald-800" : "bg-sky-50 text-sky-700"}`}>{it.type || "school"}</span></td>
                <td className="px-5 py-3 text-slate-600">{[it.city, it.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-5 py-3 text-slate-600 text-xs">{it.board || it.affiliation || "—"}</td>
                <td className="px-5 py-3 text-right flex justify-end gap-1">
                  <button onClick={() => setEditing(it)} className="p-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700" title="Edit" data-testid={`dir-edit-${it.id}`}><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(it.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" title="Delete" data-testid={`dir-delete-${it.id}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No institutions yet. Click "Add profile" to create one.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <InstitutionEditor
          institution={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function InstitutionEditor({ institution, onClose, onSaved }) {
  const isNew = !institution;
  const [f, setF] = useState({
    name: institution?.name || "",
    type: institution?.type || "school",
    logo: institution?.logo || "",
    banner: institution?.banner || "",
    description: institution?.description || "",
    description_html: institution?.description_html || "",
    established_year: institution?.established_year || "",
    affiliation: institution?.affiliation || "",
    board: institution?.board || "",
    principal: institution?.principal || "",
    state: institution?.state || "",
    city: institution?.city || "",
    address: institution?.address || "",
    phone: institution?.phone || "",
    email: institution?.email || "",
    website: institution?.website || "",
    courses: (institution?.courses || []).join(", "),
    facilities: (institution?.facilities || []).join(", "),
    achievements: (institution?.achievements || []).join(", "),
    gallery: institution?.gallery || [],
    top_photos: institution?.top_photos || [],
    col_left_html: institution?.col_left_html || "",
    col_right_html: institution?.col_right_html || "",
    masthead_year: institution?.masthead_year || new Date().getFullYear(),
    featured: !!institution?.featured,
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        established_year: f.established_year ? parseInt(f.established_year, 10) : null,
        courses: f.courses.split(",").map(s => s.trim()).filter(Boolean),
        facilities: f.facilities.split(",").map(s => s.trim()).filter(Boolean),
        achievements: f.achievements.split(",").map(s => s.trim()).filter(Boolean),
        gallery: Array.isArray(f.gallery) ? f.gallery : [],
        top_photos: Array.isArray(f.top_photos) ? f.top_photos.filter(Boolean) : [],
        col_left_html: f.col_left_html || "",
        col_right_html: f.col_right_html || "",
        masthead_year: f.masthead_year ? parseInt(f.masthead_year, 10) : null,
      };
      if (isNew) await api.post("/institutions", payload);
      else await api.put(`/institutions/${institution.id}`, payload);
      toast.success(isNew ? "Institution added" : "Institution updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="institution-editor-modal">
      <div className="bg-white rounded-3xl max-w-3xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="inst-close"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">{isNew ? "Add institution" : "Edit institution"}</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-6">{isNew ? "New profile" : institution.name}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-600 mb-1 block">Name <span className="text-red-500">*</span></label>
              <input required autoFocus value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="inst-name"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Type</label>
              <select value={f.type} onChange={e => setF({...f, type: e.target.value})} className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm" data-testid="inst-type">
                <option value="school">School (K-12)</option>
                <option value="college">College / University</option>
              </select>
            </div>
          </div>

          <ImageUpload label="Logo (square)" value={f.logo} onChange={(v) => setF({...f, logo: v})} testId="inst-logo" height="h-32"/>
          <ImageUpload label="Banner / cover image" value={f.banner} onChange={(v) => setF({...f, banner: v})} testId="inst-banner" height="h-40"/>

          <div>
            <label className="text-xs text-slate-600 mb-1 block">Description (rich text)</label>
            <textarea rows={2} value={f.description} onChange={e => setF({...f, description: e.target.value})} placeholder="One-line plain summary shown in listings" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm mb-3" data-testid="inst-description"/>
            <RichTextEditorPro value={f.description_html} onChange={(html) => setF({...f, description_html: html})} minHeight="240px" testId="inst-description-html"/>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Established year</label>
              <input type="number" value={f.established_year} onChange={e => setF({...f, established_year: e.target.value})} placeholder="e.g. 1962" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Board / Affiliation</label>
              <input value={f.board} onChange={e => setF({...f, board: e.target.value})} placeholder="CBSE / ICSE / IB / State" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Affiliation (universities)</label>
              <input value={f.affiliation} onChange={e => setF({...f, affiliation: e.target.value})} placeholder="e.g. UGC, AICTE" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Principal / VC</label>
              <input value={f.principal} onChange={e => setF({...f, principal: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">City</label>
              <input value={f.city} onChange={e => setF({...f, city: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">State</label>
              <input value={f.state} onChange={e => setF({...f, state: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600 mb-1 block">Address</label>
            <input value={f.address} onChange={e => setF({...f, address: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input type="tel" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} placeholder="Phone" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input type="email" value={f.email} onChange={e => setF({...f, email: e.target.value})} placeholder="Email" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.website} onChange={e => setF({...f, website: e.target.value})} placeholder="https://website.edu" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>

          <div>
            <label className="text-xs text-slate-600 mb-1 block">Courses / Programs (comma separated)</label>
            <input value={f.courses} onChange={e => setF({...f, courses: e.target.value})} placeholder="B.Tech CSE, MBA, MBBS" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Facilities (comma separated)</label>
            <input value={f.facilities} onChange={e => setF({...f, facilities: e.target.value})} placeholder="Library, Sports, Labs, Hostel" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Achievements (comma separated)</label>
            <input value={f.achievements} onChange={e => setF({...f, achievements: e.target.value})} placeholder="NIRF #12, ISO 9001" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          </div>
          <GalleryUpload label="Photo gallery" value={f.gallery} onChange={(arr) => setF({...f, gallery: arr})} testId="inst-gallery"/>

          <label className="flex items-center gap-2 text-sm text-slate-700 pt-2">
            <input type="checkbox" checked={!!f.featured} onChange={e => setF({...f, featured: e.target.checked})} data-testid="inst-featured"/>
            <span className="font-semibold">Featured</span>
            <span className="text-xs text-slate-500">(pin to top of the directory search results)</span>
          </label>

          <MagazineLayoutFields
            photos={f.top_photos}
            onPhotosChange={(arr) => setF({...f, top_photos: arr})}
            leftHtml={f.col_left_html}
            onLeftChange={(html) => setF({...f, col_left_html: html})}
            rightHtml={f.col_right_html}
            onRightChange={(html) => setF({...f, col_right_html: html})}
            testId="inst-mag"
          />

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="inst-save"><Save className="w-4 h-4"/>{busy ? "Saving…" : (isNew ? "Create profile" : "Save changes")}</button>
            <button onClick={() => setPreview(true)} className="px-4 py-2.5 rounded-full bg-slate-900 text-yellow-300 text-sm font-semibold flex items-center gap-1" data-testid="inst-preview"><Eye className="w-4 h-4"/>Preview</button>
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </div>
      {preview && (
        <ProfilePreviewOverlay
          masthead={f.name}
          year={f.masthead_year}
          photos={f.top_photos}
          leftHtml={f.col_left_html}
          rightHtml={f.col_right_html}
          onBack={() => setPreview(false)}
          testId="inst-preview-overlay"
        />
      )}
    </div>
  );
}


// ================= Suppliers Manager =================
function SuppliersManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/suppliers").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!confirm("Delete this supplier profile?")) return;
    try { await api.delete(`/suppliers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4" data-testid="suppliers-manager">
      <div className="card-soft p-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-sky-700 mb-1">Suppliers</div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">Publishers · EdTech vendors · Uniform makers · Furniture · Labs</h3>
          <p className="text-sm text-slate-600 mt-1">Listed on public <code className="bg-slate-100 px-1 rounded text-xs">/suppliers</code> and each has a profile at <code className="bg-slate-100 px-1 rounded text-xs">/supplier/&lt;slug&gt;</code>.</p>
        </div>
        <button onClick={() => setEditing("new")} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1" data-testid="sup-add-btn"><Plus className="w-4 h-4"/>Add supplier</button>
      </div>
      <div className="card-soft overflow-hidden" data-testid="sup-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Location</th>
              <th className="px-5 py-3">Website</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`sup-row-${it.id}`}>
                <td className="px-5 py-3 font-editorial font-semibold text-slate-900"><Link to={`/supplier/${it.slug}`} className="title-link">{it.name}</Link></td>
                <td className="px-5 py-3"><span className="text-[10px] overline px-2 py-1 rounded-full bg-sky-50 text-sky-700">{it.category}</span></td>
                <td className="px-5 py-3 text-slate-600">{[it.city, it.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-5 py-3 text-xs text-slate-500 truncate max-w-[220px]">{it.website ? <a href={it.website} target="_blank" rel="noreferrer" className="underline">{it.website}</a> : "—"}</td>
                <td className="px-5 py-3 text-right flex justify-end gap-1">
                  <button onClick={() => setEditing(it)} className="p-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700" title="Edit" data-testid={`sup-edit-${it.id}`}><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(it.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" title="Delete" data-testid={`sup-delete-${it.id}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No suppliers yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <SupplierEditor supplier={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>
      )}
    </div>
  );
}

function SupplierEditor({ supplier, onClose, onSaved }) {
  const isNew = !supplier;
  const [f, setF] = useState({
    name: supplier?.name || "",
    category: supplier?.category || "edtech",
    logo: supplier?.logo || "",
    banner: supplier?.banner || "",
    description: supplier?.description || "",
    description_html: supplier?.description_html || "",
    gallery: supplier?.gallery || [],
    products: (supplier?.products || []).join(", "),
    services: (supplier?.services || []).join(", "),
    website: supplier?.website || "",
    email: supplier?.email || "",
    phone: supplier?.phone || "",
    city: supplier?.city || "",
    state: supplier?.state || "",
    top_photos: supplier?.top_photos || [],
    col_left_html: supplier?.col_left_html || "",
    col_right_html: supplier?.col_right_html || "",
    masthead_year: supplier?.masthead_year || new Date().getFullYear(),
    featured: !!supplier?.featured,
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !f.category.trim()) { toast.error("Name and category are required"); return; }
    setBusy(true);
    try {
      const payload = { ...f,
        products: f.products.split(",").map(s => s.trim()).filter(Boolean),
        services: f.services.split(",").map(s => s.trim()).filter(Boolean),
        gallery: Array.isArray(f.gallery) ? f.gallery : [],
        top_photos: Array.isArray(f.top_photos) ? f.top_photos.filter(Boolean) : [],
        col_left_html: f.col_left_html || "",
        col_right_html: f.col_right_html || "",
        masthead_year: f.masthead_year ? parseInt(f.masthead_year, 10) : null,
      };
      if (isNew) await api.post("/suppliers", payload);
      else await api.put(`/suppliers/${supplier.id}`, payload);
      toast.success(isNew ? "Supplier added" : "Supplier updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="supplier-editor-modal">
      <div className="bg-white rounded-3xl max-w-3xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="sup-close"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">{isNew ? "Add supplier" : "Edit supplier"}</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-6">{isNew ? "New supplier profile" : supplier.name}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-600 mb-1 block">Name <span className="text-red-500">*</span></label>
              <input required value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="sup-name"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Category <span className="text-red-500">*</span></label>
              <select value={f.category} onChange={e => setF({...f, category: e.target.value})} className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm" data-testid="sup-category">
                <option value="edtech">EdTech</option>
                <option value="publishing">Publishing</option>
                <option value="uniforms">Uniforms</option>
                <option value="stationery">Stationery</option>
                <option value="furniture">Furniture</option>
                <option value="labs">Labs / Equipment</option>
                <option value="transport">Transport</option>
                <option value="catering">Catering</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <ImageUpload label="Logo" value={f.logo} onChange={(v) => setF({...f, logo: v})} testId="sup-logo" height="h-32"/>
          <ImageUpload label="Banner" value={f.banner} onChange={(v) => setF({...f, banner: v})} testId="sup-banner" height="h-40"/>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Description (rich text)</label>
            <textarea rows={2} value={f.description} onChange={e => setF({...f, description: e.target.value})} placeholder="One-line plain summary shown in listings" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm mb-3" data-testid="sup-description"/>
            <RichTextEditorPro value={f.description_html} onChange={(html) => setF({...f, description_html: html})} minHeight="220px" testId="sup-description-html"/>
          </div>
          <GalleryUpload label="Photo gallery" value={f.gallery} onChange={(arr) => setF({...f, gallery: arr})} testId="sup-gallery"/>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Products (comma separated)</label>
            <input value={f.products} onChange={e => setF({...f, products: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Services (comma separated)</label>
            <input value={f.services} onChange={e => setF({...f, services: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={f.website} onChange={e => setF({...f, website: e.target.value})} placeholder="Website" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.email} onChange={e => setF({...f, email: e.target.value})} placeholder="Email" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.phone} onChange={e => setF({...f, phone: e.target.value})} placeholder="Phone" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={f.city} onChange={e => setF({...f, city: e.target.value})} placeholder="City" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.state} onChange={e => setF({...f, state: e.target.value})} placeholder="State" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!f.featured} onChange={e => setF({...f, featured: e.target.checked})} data-testid="sup-featured"/>
            <span className="font-semibold">Featured</span>
            <span className="text-xs text-slate-500">(pin to top of the supplier search)</span>
          </label>

          <MagazineLayoutFields
            photos={f.top_photos}
            onPhotosChange={(arr) => setF({...f, top_photos: arr})}
            leftHtml={f.col_left_html}
            onLeftChange={(html) => setF({...f, col_left_html: html})}
            rightHtml={f.col_right_html}
            onRightChange={(html) => setF({...f, col_right_html: html})}
            testId="sup-mag"
          />

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="sup-save"><Save className="w-4 h-4"/>{busy ? "Saving…" : (isNew ? "Create" : "Save changes")}</button>
            <button onClick={() => setPreview(true)} className="px-4 py-2.5 rounded-full bg-slate-900 text-yellow-300 text-sm font-semibold flex items-center gap-1" data-testid="sup-preview"><Eye className="w-4 h-4"/>Preview</button>
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </div>
      {preview && (
        <ProfilePreviewOverlay
          masthead={f.name}
          year={f.masthead_year}
          photos={f.top_photos}
          leftHtml={f.col_left_html}
          rightHtml={f.col_right_html}
          onBack={() => setPreview(false)}
          testId="sup-preview-overlay"
        />
      )}
    </div>
  );
}

// ================= Rankings Manager =================
function RankingsManager() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [year, setYear] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/rankings", { params: { ...(category !== "all" && { category }), ...(year && { year }) } }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [category, year]);
  const del = async (id) => {
    if (!confirm("Delete this ranking entry?")) return;
    try { await api.delete(`/rankings/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const cats = Array.from(new Set(items.map(i => i.category))).filter(Boolean);
  return (
    <div className="space-y-4" data-testid="rankings-manager">
      <div className="card-soft p-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-sky-700 mb-1">Rankings</div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">NIRF / Category rankings</h3>
          <p className="text-sm text-slate-600 mt-1">Manage the ranked lists shown on the public <code className="bg-slate-100 px-1 rounded text-xs">/rankings</code> page.</p>
        </div>
        <select value={category} onChange={e => setCategory(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm" data-testid="rk-filter-cat">
          <option value="all">All categories</option>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
          <option value="engineering">engineering</option>
          <option value="management">management</option>
          <option value="medical">medical</option>
          <option value="law">law</option>
          <option value="university">university</option>
        </select>
        <input value={year} onChange={e => setYear(e.target.value)} placeholder="Year" type="number" className="w-24 px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="rk-filter-year"/>
        <button onClick={() => setEditing("new")} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1" data-testid="rk-add-btn"><Plus className="w-4 h-4"/>Add ranking</button>
      </div>
      <div className="card-soft overflow-hidden" data-testid="rk-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr>
              <th className="px-5 py-3">Rank</th>
              <th className="px-5 py-3">Institution</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Year</th>
              <th className="px-5 py-3">Score</th>
              <th className="px-5 py-3">Location</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`rk-row-${r.id}`}>
                <td className="px-5 py-3 font-editorial font-black text-sky-800">#{r.rank}</td>
                <td className="px-5 py-3 font-semibold text-slate-900">{r.institution}</td>
                <td className="px-5 py-3"><span className="text-[10px] overline px-2 py-1 rounded-full bg-sky-50 text-sky-700">{r.category}</span></td>
                <td className="px-5 py-3 text-slate-700">{r.year}</td>
                <td className="px-5 py-3 text-slate-700">{r.score}</td>
                <td className="px-5 py-3 text-slate-600 text-xs">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-5 py-3 text-right flex justify-end gap-1">
                  <button onClick={() => setEditing(r)} className="p-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700" title="Edit" data-testid={`rk-edit-${r.id}`}><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(r.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" title="Delete" data-testid={`rk-delete-${r.id}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No entries. Add one to get started.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <RankingEditor entry={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>
      )}
    </div>
  );
}

function RankingEditor({ entry, onClose, onSaved }) {
  const isNew = !entry;
  const [f, setF] = useState({
    category: entry?.category || "engineering",
    year: entry?.year || new Date().getFullYear(),
    rank: entry?.rank || 1,
    institution: entry?.institution || "",
    score: entry?.score || 0,
    state: entry?.state || "",
    city: entry?.city || "",
    type: entry?.type || "",
    ownership: entry?.ownership || "",
    naac: entry?.naac || "",
    nirf: entry?.nirf || "",
    website: entry?.website || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.institution.trim() || !f.category.trim()) { toast.error("Institution and category are required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, year: parseInt(f.year, 10) || new Date().getFullYear(), rank: parseInt(f.rank, 10) || 1, score: parseFloat(f.score) || 0 };
      if (isNew) await api.post("/rankings", payload);
      else await api.put(`/rankings/${entry.id}`, payload);
      toast.success(isNew ? "Ranking added" : "Ranking updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="ranking-editor-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="rk-close"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">{isNew ? "Add ranking" : "Edit ranking"}</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-6">{isNew ? "New ranking entry" : `#${entry.rank} ${entry.institution}`}</h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Institution <span className="text-red-500">*</span></label>
            <input required autoFocus value={f.institution} onChange={e => setF({...f, institution: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="rk-institution"/>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Rank <span className="text-red-500">*</span></label>
              <input required type="number" min="1" value={f.rank} onChange={e => setF({...f, rank: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="rk-rank"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Year</label>
              <input type="number" value={f.year} onChange={e => setF({...f, year: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="rk-year"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Score</label>
              <input type="number" step="0.01" value={f.score} onChange={e => setF({...f, score: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="rk-score"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Category <span className="text-red-500">*</span></label>
              <select value={f.category} onChange={e => setF({...f, category: e.target.value})} className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm" data-testid="rk-category">
                <option value="engineering">engineering</option>
                <option value="management">management</option>
                <option value="medical">medical</option>
                <option value="law">law</option>
                <option value="university">university</option>
                <option value="school">school</option>
                <option value="research">research</option>
                <option value="architecture">architecture</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={f.city} onChange={e => setF({...f, city: e.target.value})} placeholder="City" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.state} onChange={e => setF({...f, state: e.target.value})} placeholder="State" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <input value={f.type} onChange={e => setF({...f, type: e.target.value})} placeholder="Type (IIT/IIM…)" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.ownership} onChange={e => setF({...f, ownership: e.target.value})} placeholder="Public / Private" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.naac} onChange={e => setF({...f, naac: e.target.value})} placeholder="NAAC grade" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.nirf} onChange={e => setF({...f, nirf: e.target.value})} placeholder="NIRF band" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>
          <input value={f.website} onChange={e => setF({...f, website: e.target.value})} placeholder="Website" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="rk-save"><Save className="w-4 h-4"/>{busy ? "Saving…" : (isNew ? "Create" : "Save changes")}</button>
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ================= Institution Claims Manager =================
function ClaimsManager() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = () => api.get("/admin/institution-claims", { params: { status } }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [status]);
  const approve = async (id) => { try { await api.post(`/admin/institution-claims/${id}/approve`); toast.success("Claim approved — institution verified"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const reject = async (id) => { try { await api.post(`/admin/institution-claims/${id}/reject`); toast.success("Claim rejected"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const del = async (id) => { if (!confirm("Delete this claim record?")) return; try { await api.delete(`/admin/institution-claims/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-4" data-testid="claims-manager">
      <div className="card-soft p-6">
        <div className="overline text-sky-700 mb-1">Institution Claims</div>
        <h3 className="font-editorial text-xl font-bold text-slate-900">Verify people claiming to represent institutions</h3>
        <p className="text-sm text-slate-600 mt-1">Anyone can click "Claim this profile" on <code className="bg-slate-100 px-1 rounded text-xs">/institution/&lt;slug&gt;</code>. Approving marks the institution as verified and records the contact info.</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {["pending","approved","rejected","all"].map(s => (
          <button key={s} onClick={() => setStatus(s)} className={`px-4 py-2 rounded-full text-xs font-semibold capitalize ${status===s?"bg-sky-700 text-white":"bg-slate-100 text-slate-700 hover:bg-sky-50"}`} data-testid={`claim-filter-${s}`}>{s}</button>
        ))}
      </div>
      {items.length === 0 ? (
        <div className="card-soft p-8 text-center text-slate-500" data-testid="claims-empty">No {status} claims.</div>
      ) : (
        <div className="space-y-3">
          {items.map(c => (
            <div key={c.id} className="card-soft p-6" data-testid={`claim-${c.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`overline text-[10px] px-2 py-1 rounded-full ${c.status==="pending"?"bg-amber-50 text-amber-800":c.status==="approved"?"bg-emerald-50 text-emerald-800":"bg-red-50 text-red-700"}`}>{c.status}</span>
                <span className="overline text-slate-500 text-[10px]">Institution: <Link className="underline text-sky-700" to={`/institution/${c.institution_slug}`}>{c.institution_name}</Link></span>
                <span className="ml-auto text-[10px] text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-700">
                <div><span className="font-semibold text-sky-800">Claimant:</span> {c.claimant_name}</div>
                <div><span className="font-semibold text-sky-800">Role:</span> {c.claimant_role || "—"}</div>
                <div><span className="font-semibold text-sky-800">Email:</span> <a href={`mailto:${c.claimant_email}`} className="underline">{c.claimant_email}</a></div>
                {c.claimant_phone && <div><span className="font-semibold text-sky-800">Phone:</span> <a href={`tel:${c.claimant_phone}`} className="underline">{c.claimant_phone}</a></div>}
              </div>
              {c.message && <p className="mt-3 text-sm text-slate-600 italic">"{c.message}"</p>}
              {c.status === "pending" && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => approve(c.id)} className="px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-1" data-testid={`claim-approve-${c.id}`}><Check className="w-4 h-4"/>Approve & Verify</button>
                  <button onClick={() => reject(c.id)} className="px-4 py-2 rounded-full bg-slate-100 hover:bg-red-50 text-slate-700 text-sm font-semibold flex items-center gap-1" data-testid={`claim-reject-${c.id}`}><X className="w-4 h-4"/>Reject</button>
                </div>
              )}
              {c.status !== "pending" && (
                <button onClick={() => del(c.id)} className="mt-4 px-4 py-2 rounded-full bg-red-50 hover:bg-red-100 text-red-600 text-xs flex items-center gap-1" data-testid={`claim-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5"/>Delete record</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ================= EdTech Companies Manager =================
function EdtechManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/edtech").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!confirm("Delete this EdTech company profile?")) return;
    try { await api.delete(`/edtech/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4" data-testid="edtech-manager">
      <div className="card-soft p-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-sky-700 mb-1">EdTech Companies</div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">Startups, scale-ups & unicorns building learning tech</h3>
          <p className="text-sm text-slate-600 mt-1">Published at <code className="bg-slate-100 px-1 rounded text-xs">/edtech</code> with profiles at <code className="bg-slate-100 px-1 rounded text-xs">/edtech/&lt;slug&gt;</code>.</p>
        </div>
        <button onClick={() => setEditing("new")} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1" data-testid="edtech-add-btn"><Plus className="w-4 h-4"/>Add company</button>
      </div>
      <div className="card-soft overflow-hidden" data-testid="edtech-admin-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Tagline</th>
              <th className="px-5 py-3">Stage</th>
              <th className="px-5 py-3">HQ</th>
              <th className="px-5 py-3">Featured</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`edtech-row-${c.id}`}>
                <td className="px-5 py-3 font-editorial font-semibold text-slate-900"><Link to={`/edtech/${c.slug}`} className="title-link">{c.name}</Link></td>
                <td className="px-5 py-3 text-slate-600 truncate max-w-[280px]">{c.tagline}</td>
                <td className="px-5 py-3"><span className="text-[10px] overline px-2 py-1 rounded-full bg-sky-50 text-sky-700">{c.stage || "—"}</span></td>
                <td className="px-5 py-3 text-slate-600">{c.headquarters || "—"}</td>
                <td className="px-5 py-3">{c.featured ? <span className="text-[10px] overline px-2 py-1 rounded-full bg-amber-50 text-amber-800">featured</span> : <span className="text-xs text-slate-400">—</span>}</td>
                <td className="px-5 py-3 text-right flex justify-end gap-1">
                  <button onClick={() => setEditing(c)} className="p-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700" title="Edit" data-testid={`edtech-edit-${c.id}`}><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(c.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" title="Delete" data-testid={`edtech-delete-${c.id}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No EdTech companies yet. Click "Add company".</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <EdtechEditor company={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>
      )}
    </div>
  );
}

function EdtechEditor({ company, onClose, onSaved }) {
  const isNew = !company;
  const [f, setF] = useState({
    name: company?.name || "",
    tagline: company?.tagline || "",
    logo: company?.logo || "",
    banner: company?.banner || "",
    description: company?.description || "",
    description_html: company?.description_html || "",
    gallery: company?.gallery || [],
    founded_year: company?.founded_year || "",
    headquarters: company?.headquarters || "",
    country: company?.country || "India",
    website: company?.website || "",
    email: company?.email || "",
    phone: company?.phone || "",
    linkedin: company?.linkedin || "",
    twitter: company?.twitter || "",
    stage: company?.stage || "",
    employees: company?.employees || "",
    funding_total: company?.funding_total || "",
    valuation: company?.valuation || "",
    audience: (company?.audience || []).join(", "),
    categories: (company?.categories || []).join(", "),
    products: (company?.products || []).join(", "),
    integrations: (company?.integrations || []).join(", "),
    tech_stack: (company?.tech_stack || []).join(", "),
    founders: (company?.founders || []).join(", "),
    investors: (company?.investors || []).join(", "),
    featured: !!company?.featured,
    top_photos: company?.top_photos || [],
    col_left_html: company?.col_left_html || "",
    col_right_html: company?.col_right_html || "",
    masthead_year: company?.masthead_year || new Date().getFullYear(),
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const csv = (s) => s.split(",").map(x => x.trim()).filter(Boolean);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...f,
        founded_year: f.founded_year ? parseInt(f.founded_year, 10) : null,
        audience: csv(f.audience), categories: csv(f.categories),
        products: csv(f.products), integrations: csv(f.integrations),
        tech_stack: csv(f.tech_stack), founders: csv(f.founders),
        investors: csv(f.investors),
        gallery: Array.isArray(f.gallery) ? f.gallery : [],
        top_photos: Array.isArray(f.top_photos) ? f.top_photos.filter(Boolean) : [],
        col_left_html: f.col_left_html || "",
        col_right_html: f.col_right_html || "",
        masthead_year: f.masthead_year ? parseInt(f.masthead_year, 10) : null,
      };
      if (isNew) await api.post("/edtech", payload);
      else await api.put(`/edtech/${company.id}`, payload);
      toast.success(isNew ? "Company added" : "Company updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="edtech-editor-modal">
      <div className="bg-white rounded-3xl max-w-3xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="edtech-close"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">{isNew ? "Add EdTech company" : "Edit EdTech company"}</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-6">{isNew ? "New profile" : company.name}</h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Name <span className="text-red-500">*</span></label>
            <input required autoFocus value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="edtech-name"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Tagline</label>
            <input value={f.tagline} onChange={e => setF({...f, tagline: e.target.value})} placeholder="One-liner describing the product/mission" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="edtech-tagline"/>
          </div>
          <ImageUpload label="Logo (square)" value={f.logo} onChange={(v) => setF({...f, logo: v})} testId="edtech-logo" height="h-32"/>
          <ImageUpload label="Banner / cover" value={f.banner} onChange={(v) => setF({...f, banner: v})} testId="edtech-banner" height="h-40"/>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Description (rich text)</label>
            <textarea rows={2} value={f.description} onChange={e => setF({...f, description: e.target.value})} placeholder="One-line plain summary shown in listings" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm mb-3" data-testid="edtech-description"/>
            <RichTextEditorPro value={f.description_html} onChange={(html) => setF({...f, description_html: html})} minHeight="240px" testId="edtech-description-html"/>
          </div>
          <GalleryUpload label="Photo gallery" value={f.gallery} onChange={(arr) => setF({...f, gallery: arr})} testId="edtech-gallery"/>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <input type="number" value={f.founded_year} onChange={e => setF({...f, founded_year: e.target.value})} placeholder="Founded year" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.headquarters} onChange={e => setF({...f, headquarters: e.target.value})} placeholder="HQ city" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.country} onChange={e => setF({...f, country: e.target.value})} placeholder="Country" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <select value={f.stage} onChange={e => setF({...f, stage: e.target.value})} className="px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm">
              <option value="">Stage</option>
              <option>Bootstrapped</option><option>Pre-Seed</option><option>Seed</option><option>Series A</option><option>Series B+</option><option>Listed</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={f.employees} onChange={e => setF({...f, employees: e.target.value})} placeholder="Employees (e.g. 11-50)" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.funding_total} onChange={e => setF({...f, funding_total: e.target.value})} placeholder="Total funding ($120M)" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.valuation} onChange={e => setF({...f, valuation: e.target.value})} placeholder="Valuation ($1B)" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={f.website} onChange={e => setF({...f, website: e.target.value})} placeholder="Website" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.email} onChange={e => setF({...f, email: e.target.value})} placeholder="Email" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.phone} onChange={e => setF({...f, phone: e.target.value})} placeholder="Phone" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={f.linkedin} onChange={e => setF({...f, linkedin: e.target.value})} placeholder="LinkedIn URL" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
            <input value={f.twitter} onChange={e => setF({...f, twitter: e.target.value})} placeholder="Twitter URL" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          </div>
          <div><label className="text-xs text-slate-600 mb-1 block">Audience (comma sep — K-12, HigherEd, Test-Prep, Skilling, Corporate)</label><input value={f.audience} onChange={e => setF({...f, audience: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/></div>
          <div><label className="text-xs text-slate-600 mb-1 block">Categories (AI-Learning, LMS, Adaptive, Content, Assessment, Tutoring…)</label><input value={f.categories} onChange={e => setF({...f, categories: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/></div>
          <div><label className="text-xs text-slate-600 mb-1 block">Products (comma sep)</label><input value={f.products} onChange={e => setF({...f, products: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/></div>
          <div><label className="text-xs text-slate-600 mb-1 block">Integrations (comma sep)</label><input value={f.integrations} onChange={e => setF({...f, integrations: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/></div>
          <div><label className="text-xs text-slate-600 mb-1 block">Tech stack (comma sep)</label><input value={f.tech_stack} onChange={e => setF({...f, tech_stack: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-600 mb-1 block">Founders (comma sep)</label><input value={f.founders} onChange={e => setF({...f, founders: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/></div>
            <div><label className="text-xs text-slate-600 mb-1 block">Investors (comma sep)</label><input value={f.investors} onChange={e => setF({...f, investors: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm"/></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.featured} onChange={e => setF({...f, featured: e.target.checked})} data-testid="edtech-featured"/>Featured (pin to top of list)</label>

          <MagazineLayoutFields
            photos={f.top_photos}
            onPhotosChange={(arr) => setF({...f, top_photos: arr})}
            leftHtml={f.col_left_html}
            onLeftChange={(html) => setF({...f, col_left_html: html})}
            rightHtml={f.col_right_html}
            onRightChange={(html) => setF({...f, col_right_html: html})}
            testId="edtech-mag"
          />

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="edtech-save"><Save className="w-4 h-4"/>{busy ? "Saving…" : (isNew ? "Create profile" : "Save changes")}</button>
            <button onClick={() => setPreview(true)} className="px-4 py-2.5 rounded-full bg-slate-900 text-yellow-300 text-sm font-semibold flex items-center gap-1" data-testid="edtech-preview"><Eye className="w-4 h-4"/>Preview</button>
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </div>
      {preview && (
        <ProfilePreviewOverlay
          masthead={f.name}
          year={f.masthead_year}
          photos={f.top_photos}
          leftHtml={f.col_left_html}
          rightHtml={f.col_right_html}
          onBack={() => setPreview(false)}
          testId="edtech-preview-overlay"
        />
      )}
    </div>
  );
}


// ================= Ads Manager =================
const AD_PLACEMENTS = [
  { k: "home-top", l: "Home – top banner (below hero)" },
  { k: "home-inline", l: "Home – mid-feed banner" },
  { k: "article-inline", l: "Article – in-content" },
  { k: "sidebar", l: "Sidebar (right column)" },
  { k: "footer", l: "Footer banner" },
];

function AdsManager() {
  const [items, setItems] = useState([]);
  const [placement, setPlacement] = useState("all");
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/ads", { params: placement !== "all" ? { placement } : {} }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [placement]);
  const del = async (id) => {
    if (!confirm("Delete this ad?")) return;
    try { await api.delete(`/ads/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const toggleActive = async (ad) => {
    try {
      await api.put(`/ads/${ad.id}`, { ...ad, active: !ad.active });
      toast.success(!ad.active ? "Ad enabled" : "Ad paused");
      load();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="ads-manager">
      <div className="card-soft p-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-sky-700 mb-1">Advertisements</div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">Self-managed banner ads</h3>
          <p className="text-sm text-slate-600 mt-1">Upload a creative, pick a placement, and it goes live. Impressions and clicks are tracked automatically. Recommended sizes: <strong>728×90</strong> or <strong>970×250</strong> for banners, <strong>300×250</strong> for sidebar.</p>
        </div>
        <select value={placement} onChange={e => setPlacement(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm" data-testid="ads-filter-placement">
          <option value="all">All placements</option>
          {AD_PLACEMENTS.map(p => <option key={p.k} value={p.k}>{p.l}</option>)}
        </select>
        <button onClick={() => setEditing("new")} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1" data-testid="ad-add-btn"><Plus className="w-4 h-4"/>New ad</button>
      </div>
      <div className="card-soft overflow-hidden" data-testid="ads-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr>
              <th className="px-5 py-3">Preview</th>
              <th className="px-5 py-3">Title / Sponsor</th>
              <th className="px-5 py-3">Placement</th>
              <th className="px-5 py-3">Impr.</th>
              <th className="px-5 py-3">Clicks</th>
              <th className="px-5 py-3">CTR</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(a => {
              const src = a.image ? (a.image.startsWith("http") ? a.image : `${process.env.REACT_APP_BACKEND_URL}${a.image}`) : "";
              const ctr = a.impressions ? ((a.clicks / a.impressions) * 100).toFixed(2) + "%" : "—";
              return (
                <tr key={a.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`ad-row-${a.id}`}>
                  <td className="px-5 py-3">{src ? <img src={src} alt="" className="w-24 h-12 object-cover rounded-lg border border-slate-100"/> : <span className="text-xs text-slate-400">no image</span>}</td>
                  <td className="px-5 py-3">
                    <div className="font-editorial font-semibold text-slate-900">{a.title}</div>
                    {a.sponsor && <div className="text-xs text-slate-500">{a.sponsor}</div>}
                  </td>
                  <td className="px-5 py-3"><span className="text-[10px] overline px-2 py-1 rounded-full bg-sky-50 text-sky-700">{a.placement}</span></td>
                  <td className="px-5 py-3 text-slate-700">{a.impressions || 0}</td>
                  <td className="px-5 py-3 text-slate-700">{a.clicks || 0}</td>
                  <td className="px-5 py-3 text-slate-600">{ctr}</td>
                  <td className="px-5 py-3"><span className={`text-[10px] overline px-2 py-1 rounded-full ${a.active ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{a.active ? "active" : "paused"}</span></td>
                  <td className="px-5 py-3 text-right flex justify-end gap-1">
                    <button onClick={() => toggleActive(a)} className="p-2 rounded-lg bg-slate-100 hover:bg-emerald-100 text-slate-600" title={a.active ? "Pause" : "Enable"} data-testid={`ad-toggle-${a.id}`}>{a.active ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}</button>
                    <button onClick={() => setEditing(a)} className="p-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700" title="Edit" data-testid={`ad-edit-${a.id}`}><Edit3 className="w-4 h-4"/></button>
                    <button onClick={() => del(a.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" title="Delete" data-testid={`ad-delete-${a.id}`}><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500">No ads yet. Click "New ad" to create one.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <AdEditor ad={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>
      )}
    </div>
  );
}

function AdEditor({ ad, onClose, onSaved }) {
  const isNew = !ad;
  const [f, setF] = useState({
    title: ad?.title || "",
    sponsor: ad?.sponsor || "",
    image: ad?.image || "",
    image_mobile: ad?.image_mobile || "",
    link_url: ad?.link_url || "",
    placement: ad?.placement || "home-top",
    active: ad?.active !== false,
    order: ad?.order || 0,
    starts_at: ad?.starts_at || "",
    ends_at: ad?.ends_at || "",
    open_in_new_tab: ad?.open_in_new_tab !== false,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim() || !f.image || !f.link_url.trim()) { toast.error("Title, image and link URL are required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, order: parseInt(f.order, 10) || 0 };
      if (isNew) await api.post("/ads", payload);
      else await api.put(`/ads/${ad.id}`, payload);
      toast.success(isNew ? "Ad created" : "Ad updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="ad-editor-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="ad-close"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">{isNew ? "New ad" : "Edit ad"}</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-6">{isNew ? "Upload a creative" : ad.title}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Title (internal) <span className="text-red-500">*</span></label>
              <input required autoFocus value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="ad-title"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Sponsor / Brand</label>
              <input value={f.sponsor} onChange={e => setF({...f, sponsor: e.target.value})} placeholder="Displayed on 'ad · Brand' pill" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="ad-sponsor"/>
            </div>
          </div>
          <ImageUpload label="Desktop banner image (recommended: 970×250 or 728×90 for header/inline · 300×250 for sidebar)" value={f.image} onChange={(v) => setF({...f, image: v})} testId="ad-image" height="h-40"/>
          <ImageUpload label="Mobile banner (optional, e.g. 320×100)" value={f.image_mobile} onChange={(v) => setF({...f, image_mobile: v})} testId="ad-image-mobile" height="h-32"/>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Landing URL <span className="text-red-500">*</span></label>
              <input required value={f.link_url} onChange={e => setF({...f, link_url: e.target.value})} placeholder="https://sponsor.example.com/…" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="ad-link"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Placement <span className="text-red-500">*</span></label>
              <select value={f.placement} onChange={e => setF({...f, placement: e.target.value})} className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm" data-testid="ad-placement">
                {AD_PLACEMENTS.map(p => <option key={p.k} value={p.k}>{p.l}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Starts (optional)</label>
              <input type="date" value={f.starts_at} onChange={e => setF({...f, starts_at: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="ad-starts"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Ends (optional)</label>
              <input type="date" value={f.ends_at} onChange={e => setF({...f, ends_at: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="ad-ends"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Order (lower = higher priority)</label>
              <input type="number" value={f.order} onChange={e => setF({...f, order: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="ad-order"/>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.active} onChange={e => setF({...f, active: e.target.checked})} data-testid="ad-active"/>Active (live now)</label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.open_in_new_tab} onChange={e => setF({...f, open_in_new_tab: e.target.checked})} data-testid="ad-new-tab"/>Open link in new tab</label>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="ad-save"><Save className="w-4 h-4"/>{busy ? "Saving…" : (isNew ? "Create ad" : "Save changes")}</button>
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}



// ================= Pages Manager =================
function PagesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/pages").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id, isSystem) => {
    if (isSystem) { toast.error("System pages (About, Contact) can be edited but not deleted."); return; }
    if (!confirm("Delete this page? This cannot be undone.")) return;
    try { await api.delete(`/admin/pages/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4" data-testid="pages-manager">
      <div className="card-soft p-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-sky-700 mb-1">Pages</div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">About · Contact · Custom pages</h3>
          <p className="text-sm text-slate-600 mt-1">Publish rich-text pages. About lives at <code className="bg-slate-100 px-1 rounded text-xs">/about</code>, Contact at <code className="bg-slate-100 px-1 rounded text-xs">/contact</code>, and any custom page at <code className="bg-slate-100 px-1 rounded text-xs">/page/&lt;slug&gt;</code>.</p>
        </div>
        <button onClick={() => setEditing("new")} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1" data-testid="page-add-btn"><Plus className="w-4 h-4"/>Add page</button>
      </div>
      <div className="card-soft overflow-hidden" data-testid="pages-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
            <tr>
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Slug / URL</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`page-row-${p.slug}`}>
                <td className="px-5 py-3 font-editorial font-semibold text-slate-900">{p.title}</td>
                <td className="px-5 py-3 text-slate-600"><Link to={p.is_system ? `/${p.slug}` : `/page/${p.slug}`} className="text-sky-700 hover:underline text-xs">/{p.is_system ? p.slug : `page/${p.slug}`}</Link></td>
                <td className="px-5 py-3"><span className="text-[10px] overline px-2 py-1 rounded-full bg-sky-50 text-sky-700">{p.is_system ? "System" : "Custom"}</span></td>
                <td className="px-5 py-3">{p.published ? <span className="text-[10px] overline px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">Published</span> : <span className="text-[10px] overline px-2 py-1 rounded-full bg-slate-100 text-slate-600">Draft</span>}</td>
                <td className="px-5 py-3 text-right flex justify-end gap-1">
                  <button onClick={() => setEditing(p)} className="p-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700" title="Edit" data-testid={`page-edit-${p.slug}`}><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(p.id, p.is_system)} className={`p-2 rounded-lg ${p.is_system ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-red-50 hover:bg-red-100 text-red-600"}`} title={p.is_system ? "System page — cannot be deleted" : "Delete"} data-testid={`page-delete-${p.slug}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No pages yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <PageEditor page={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>
      )}
    </div>
  );
}

function PageEditor({ page, onClose, onSaved }) {
  const isNew = !page;
  const isSystem = !!page?.is_system;
  const [f, setF] = useState({
    slug: page?.slug || "",
    title: page?.title || "",
    content_html: page?.content_html || "",
    published: page?.published !== false,
    order: page?.order ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, order: parseInt(f.order, 10) || 0 };
      if (isNew) await api.post("/admin/pages", payload);
      else await api.put(`/admin/pages/${page.id}`, payload);
      toast.success(isNew ? "Page created" : "Page updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="page-editor-modal">
      <div className="bg-white rounded-3xl max-w-5xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="page-close"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">{isNew ? "Add page" : "Edit page"}{isSystem && " · System"}</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-6">{isNew ? "New page" : page.title}</h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Title <span className="text-red-500">*</span></label>
            <input required autoFocus value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="page-title-input"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Slug (URL) {isSystem && <span className="text-slate-400">— locked</span>}</label>
            <input value={f.slug} onChange={e => setF({...f, slug: e.target.value})} disabled={isSystem} placeholder="about-us" className={`w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm ${isSystem ? "bg-slate-50 text-slate-500" : ""}`} data-testid="page-slug-input"/>
            <div className="text-[11px] text-slate-500 mt-1">Public URL: <code className="bg-slate-100 px-1 rounded">/{isSystem ? f.slug : (f.slug ? `page/${f.slug}` : "page/…")}</code></div>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Body (rich text · fonts, sizes, colours, images, tables, links)</label>
            <RichTextEditorPro value={f.content_html} onChange={(html) => setF({...f, content_html: html})} minHeight="400px" testId="page-content-editor"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.published} onChange={e => setF({...f, published: e.target.checked})} data-testid="page-published"/>Published (visible to public)</label>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Order</label>
              <input type="number" value={f.order} onChange={e => setF({...f, order: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="page-order"/>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="page-save"><Save className="w-4 h-4"/>{busy ? "Saving…" : (isNew ? "Create page" : "Save changes")}</button>
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
